import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  mediaVariantIds,
  isVariantOnMedia,
  findMediaForVariant,
  assignVariantToMedia,
  mediaPayloadItem,
  normalizeMediaItem,
  otherVariantNames,
} from '../../utils/mediaUtils';
import { useSelector } from 'react-redux';
import './ManageProducts.css';
import { getOptionValue, buildOptionValuesFromOptionsAndCombo, getProductOptions } from '../../utils/optionUtils';
import {
  listCategories,
  getProductReviewSummary,
  getCurrentUserRole,
  updateProduct,
  updateProductOptions,
  updateVariantField,
  assignVariantImage,
  reorderProductImages
} from '../../services/shipturtleApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { exportProductLogs } from '../../api/api';
import { hasBlobUrls } from '../../utils/helpers';
import HTMLEditor from './HTMLEditor';

// Catalog status (terminal_status: draft/active/unlisted/archived/deleted) badge class.
const catalogStatusClass = (terminalStatus) => {
  switch (terminalStatus) {
    case 'active':   return 'statusLive';
    case 'draft':    return 'statusDraft';
    case 'archived': return 'statusInactive';
    case 'unlisted': return 'statusInactive';
    case 'deleted':  return 'statusDeleted';
    default:         return 'statusDefault';
  }
};

const formatStatusLabel = (value) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';

// Helper function to map API response to form data
const mapApiResponseToFormData = (apiProduct) => {
  if (!apiProduct) return {};

  // Extract first variant data if available
  const firstVariant = apiProduct.variants && apiProduct.variants.length > 0 ? apiProduct.variants[0] : {};

  return {
    // Overview
    title: apiProduct.title || '',
    description: apiProduct.description || '',
    category: apiProduct.category?.slug || apiProduct.category?.name || '',
    categoryId: apiProduct.category?.id || '',
    categoryName: apiProduct.category?.name || '',
    tags: apiProduct.tags || [],

    // Media
    images: (apiProduct.media || []).map((media, idx) => ({
      id: media.id,
      url: media.url,
      alt: media.alt_text || media.url,
      is_primary: media.is_primary || false,
      variant_ids: mediaVariantIds(media),
      variant_id: mediaVariantIds(media)[0] || null,
      position: media.position ?? idx,
    })),

    // Core Details
    features: apiProduct.features || '',
    careInstructions: apiProduct.care_instructions || '',

    // Inventory & Pricing - from first variant
    inventory: firstVariant.meta?.inventory || 0,
    sku: firstVariant.sku || '',
    variantName: firstVariant.variant_name || firstVariant.name || '',
    price: firstVariant.price || '',
    compareAtPrice: firstVariant.compare_price || '',

    // Shipping & Package Details - from first variant
    weight: firstVariant.weight_kg || '',
    dimensions: {
      length: firstVariant.length_cm || '',
      width: firstVariant.width_cm || '',
      height: firstVariant.height_cm || ''
    },
    hsCode: firstVariant.hs_code || '',

    // Commissions & Fees - from first variant
    brandCommission: firstVariant.commission_pct || '',
    gstRate: firstVariant.gst_rate ?? '',
    merchantSplit: apiProduct.merchant_split || '',

    // Vendor info
    vendorId: apiProduct.vendor?.id || '',
    vendorName: apiProduct.vendor?.brand_name || apiProduct.vendor?.company_name || '',
    // vendor remarks may be stored either as a top-level field or inside product.meta
    vendorRemarks: apiProduct.vendor_remarks || apiProduct.meta?.vendor_remarks || '',

    // Meta Fields - only from product meta (variant meta only has inventory)
    metaFields: (() => {
      const meta = { ...(apiProduct.meta || {}) };
      // Remove inventory, tags, and options from metaFields as they're separate fields
      delete meta.inventory;
      delete meta.tags;
      delete meta.options;
      return meta;
    })(),

    // Status and timestamps
    // The API returns `terminal_status` (catalog: draft/active/unlisted/archived/deleted)
    // and `state` (approval: pending/active) — never a bare `status`/`product_state`.
    status: apiProduct.terminal_status || 'draft',
    productState: apiProduct.state || 'active',
    publishedAt: apiProduct.published_at || null,
    createdAt: apiProduct.created_at || '',
    updatedAt: apiProduct.updated_at || '',

    // User info
    createdBy: apiProduct.created_by ? {
      id: apiProduct.created_by.id,
      name: apiProduct.created_by.name
    } : null,
    updatedBy: apiProduct.updated_by ? {
      id: apiProduct.updated_by.id,
      name: apiProduct.updated_by.name
    } : null,

    // Variant info
    variantId: firstVariant.id || '',
    variantVersion: firstVariant.version || 1,

    // Has variants flag (from backend or infer from variant count)
    hasVariants: apiProduct.has_variants !== undefined ? apiProduct.has_variants : (apiProduct.variants && apiProduct.variants.length > 1),

    // Shopify integration
    shopifyId: apiProduct.shopify_id || null,
    shopifyVariantId: firstVariant.shopify_variant_id || null,

    // Audit & History
    auditHistory: (apiProduct.auditHistory || []).map(entry => ({
      action: entry.action || '',
      user: entry.user || '',
      user_role: entry.user_role || '',
      user_id: entry.user_id || '',
      timestamp: entry.timestamp || '',
      changes: entry.changes || {},
      reason: entry.reason || '',
      payload: entry.payload || {},
      ip_address: entry.ip_address || ''
    })),

    // Store the full API response for reference
    _apiProduct: apiProduct,

    // Product ID
    id: apiProduct.id || '',
    version: apiProduct.version || 1,

    // Product Options (from the option tables; meta.options only as fallback)
    options: getProductOptions(apiProduct)
  };
};

// Helper function to map form data to API payload format
const mapFormDataToApiPayload = (formData, reason = '') => {
  if (!formData) return {};

  // Helper function to normalize empty values (treat null, undefined, empty string as same)
  const normalizeValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '—') {
      return null;
    }
    return value;
  };

  // Helper to parse numeric values
  const parseNumeric = (value) => {
    if (value === null || value === undefined || value === '' || value === '—') {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  // weight_kg is optional. The backend model stores NULL for "no weight" and rejects a
  // stored 0 (validator requires >= 0.001). So a 0/negative/blank means "no weight" and
  // must be sent as null, never as the number 0.
  const parseWeight = (value) => {
    const parsed = parseNumeric(value);
    return parsed !== null && parsed > 0 ? parsed : null;
  };

  const sourceVariants = (formData.allVariants && formData.allVariants.length > 0)
    ? formData.allVariants
    : [];

  // Build the payload matching backend expectations
  const payload = {
    // Product fields
    title: normalizeValue(formData.title) || '',
    description: normalizeValue(formData.description) || '',
    category_id: normalizeValue(formData.categoryId),
    // Catalog status (draft/active/unlisted/...). Backend field is `terminal_status`;
    // ProductService.update diffs it like any other field and — because it's a
    // changed field — the save moves the product to pending approval, same as any
    // other edit. It never pushes to the store itself; that only happens on admin
    // approval.
    terminal_status: normalizeValue(formData.status),

    // Meta fields - custom fields only (excluding inventory and tags)
    meta: (() => {
      const meta = { ...(formData.metaFields || {}) };
      // Ensure inventory is not in meta at product level
      delete meta.inventory;
      // Remove tags from regular meta fields (tags are handled separately)
      delete meta.tags;
      // Remove vendor_remarks from regular meta fields (handled separately)
      delete meta.vendor_remarks;
      // Add tags to product meta if present
      if (formData.tags && formData.tags.length > 0) {
        meta.tags = formData.tags;
      }
      // Add vendor_remarks to product meta if present
      if (formData.vendorRemarks) {
        meta.vendor_remarks = normalizeValue(formData.vendorRemarks) || '';
      }
      // Add options to product meta if present
      if (formData.options && formData.options.length > 0) {
        meta.options = formData.options;
      }
      console.log('[SAVE] Product meta being sent:', meta);
      return meta;
    })(),

    // Media/Images - use allMediaItems from formData if available, otherwise use formData.images
    // Always send id for existing media so backend matches by id (not URL).
    // Omit id only for newly uploaded images (no id yet) — backend will INSERT and return the new id.
    media: (formData.allMediaItems && formData.allMediaItems.length > 0 ? formData.allMediaItems : formData.images || [])
      .filter(img => img.url)
      .map((img, idx) => {
        // Shared helper: variant_ids authoritative, variant_id a legacy mirror.
        return {
          ...mediaPayloadItem(img, idx),
          alt_text: normalizeValue(img.alt || img.alt_text) || '',
          position: idx,
        };
      }),

    // Variants - use allVariants if available, otherwise reconstruct from form data
    variants: sourceVariants.length > 0
      ? sourceVariants.map(variant => {
        // Get option_values from variant - check multiple possible locations
        const optionValues = variant.option_values ||
          (variant.meta && variant.meta.option_values) ||
          null;

        // Build variant meta - merge existing meta and ensure inventory and option_values are inside
        const variantMeta = {
          ...(variant.meta || {}),
          inventory: parseInt(variant.meta?.inventory ?? formData.inventory ?? 0)
        };

        if (optionValues) {
          variantMeta.option_values = optionValues;
        }

        // Get variant_name - prefer explicit variant_name, fall back to name
        const variantName = variant.variant_name || variant.variantName || variant.name || '';

        return {
          id: variant.id || null,
          ...(variant.tempId ? { temp_id: variant.tempId } : {}),
          ...(variant._delete ? { _delete: true } : {}),
          sku: normalizeValue(variant.sku) || normalizeValue(formData.sku) || '',
          variant_name: normalizeValue(variantName),
          price: parseNumeric(variant.price) ?? parseNumeric(formData.price) ?? 0,
          compare_price: parseNumeric(variant.compare_price) ?? parseNumeric(formData.compareAtPrice),
          weight_kg: parseWeight(variant.weight_kg) ?? parseWeight(formData.weight),
          length_cm: parseNumeric(variant.length_cm) ?? parseNumeric(formData.dimensions?.length),
          width_cm: parseNumeric(variant.width_cm) ?? parseNumeric(formData.dimensions?.width),
          height_cm: parseNumeric(variant.height_cm) ?? parseNumeric(formData.dimensions?.height),
          hs_code: normalizeValue(variant.hs_code) || normalizeValue(formData.hsCode) || '',
          commission_pct: parseNumeric(variant.commission_pct) ?? parseNumeric(formData.brandCommission),
          gst_rate: parseNumeric(
            variant.gst_rate !== undefined && variant.gst_rate !== null
              ? variant.gst_rate
              : formData.gstRate
          ),
          meta: variantMeta
        };
      })
      : [{
        id: formData.variantId || null,
        sku: normalizeValue(formData.sku) || '',
        variant_name: normalizeValue(formData.variantName) || '',
        price: parseNumeric(formData.price) || 0,
        compare_price: parseNumeric(formData.compareAtPrice),
        weight_kg: parseWeight(formData.weight),
        length_cm: parseNumeric(formData.dimensions?.length),
        width_cm: parseNumeric(formData.dimensions?.width),
        height_cm: parseNumeric(formData.dimensions?.height),
        hs_code: normalizeValue(formData.hsCode) || '',
        commission_pct: parseNumeric(formData.brandCommission),
        gst_rate: parseNumeric(formData.gstRate),
        meta: {
          // Store inventory in variant meta only
          inventory: parseInt(formData.inventory) || 0
          // Do NOT include other meta fields in variant meta - they belong to product meta
        }
      }],

    // Product Options
    options: formData.options || [],
    has_variants: sourceVariants.length > 1 || (formData.options || []).length > 0,

    // Include reason for audit (required by backend for admin edits)
    reason: reason || 'Product updated'
  };

  console.log('[SAVE] Full payload being sent:', JSON.stringify(payload, null, 2));

  return payload;
};

/**
 * Helper component for the variant table
 */
const VariantTable = ({
  allVariants,
  options,
  selectedVariantRowIndexes,
  allMediaItems,
  variantGroupBy,
  expandedVariantGroups,
  validationErrors,
  variantErrors,
  savingVariantField,
  handleVariantFieldChange,
  toggleProductVariantRowSelection,
  handleProductVariantsHeaderSelectionClick,
  setVariantGroupBy,
  setExpandedVariantGroups,
  setImagePickerVariant,
  setShowImagePickerModal,
  openVariantDetailsModal,
  setHasChanges,
  expandedVariantId,
  setExpandedVariantId,
  setAllVariants,
  onDeleteVariant,
}) => {
  const variants = (allVariants || []).filter(v => !v._delete);
  const optionNames = (options || []).map((option) => option.name).filter(Boolean);

  const totalVariants = variants.length;
  let variantsSelectedCount = 0;
  for (let i = 0; i < totalVariants; i++) {
    if (selectedVariantRowIndexes.has(i)) variantsSelectedCount++;
  }
  const variantsAllSelected =
    totalVariants > 0 && variantsSelectedCount === totalVariants;
  const variantsPartiallySelected =
    variantsSelectedCount > 0 && variantsSelectedCount < totalVariants;

  const calculateTotalInventory = () => {
    return variants.reduce((sum, v) => sum + (parseInt(v.meta?.inventory) || 0), 0);
  };

  // Returns { url, isAssigned }. isAssigned=false means the url (if any) is
  // just a product-level image shown as a preview placeholder — it is NOT
  // actually bound to this variant in the backend (TerminalProductMedia.variant
  // is null). Callers MUST render this differently from a real assignment, or
  // the user has no way to tell "this variant has no image yet" from "this
  // variant's image is X" — which is exactly what caused variant-image
  // assignments to look correct on screen while the DB had nothing set.
  const getVariantImage = (variant) => {
    if (variant.image_id) {
      const byId = allMediaItems.find(m => m.id === variant.image_id);
      if (byId) return { url: byId.url, isAssigned: true };
    }
    // Fallback: scan the full assignment set for products where image_id is not set.
    // Matching the scalar alone reported "unassigned" for every variant but the first
    // on a shared image.
    const byVariantId = findMediaForVariant(allMediaItems, variant.id || variant.tempId);
    if (byVariantId) return { url: byVariantId.url, isAssigned: true };
    const firstProductImage = allMediaItems.find(m => mediaVariantIds(m).length === 0);
    return { url: firstProductImage ? firstProductImage.url : null, isAssigned: false };
  };

  const handleToggleExpand = (v) => {
    const vid = v.id || v.tempId;
    if (expandedVariantId === vid) {
      setExpandedVariantId(null);
    } else {
      setExpandedVariantId(vid);
    }
  };

  return (
    <div className="variantTableContainer">
      {optionNames.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
            Group by
            <select
              className="formInput"
              style={{ width: '180px' }}
              value={variantGroupBy || optionNames[0]}
              onChange={(e) => setVariantGroupBy(e.target.value)}
            >
              {optionNames.map((optionName) => (
                <option key={optionName} value={optionName}>{optionName}</option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="tableWrapper">
        <table className="variantTable">
          <thead>
            <tr>
              <th className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="variantsCompactHeaderTriCheckbox"
                  role="checkbox"
                  aria-checked={variantsPartiallySelected ? 'mixed' : variantsAllSelected}
                  aria-label={
                    variantsAllSelected ? 'Deselect all variants' : 'Select all variants'
                  }
                  disabled={totalVariants === 0}
                  onClick={handleProductVariantsHeaderSelectionClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductVariantsHeaderSelectionClick(e);
                    }
                  }}
                >
                  <span className="variantsCompactHeaderTriCheckbox-box" aria-hidden>
                    {variantsAllSelected ? (
                      <span className="variantsCompactHeaderTriCheckbox-check">✓</span>
                    ) : variantsPartiallySelected ? (
                      <span className="variantsCompactHeaderTriCheckbox-dash">−</span>
                    ) : null}
                  </span>
                </button>
              </th>
              <th>Image</th>
              <th>Variant</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Inventory</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v, idx) => {
              const groupName = variantGroupBy || optionNames[0];
              const groupValue = groupName ? (getOptionValue(v, groupName) || 'Other') : null;
              const isFirstInGroup = groupValue
                ? variants.findIndex((item) => ((getOptionValue(item, groupName) || 'Other') === groupValue)) === idx
                : false;
              const isExpandedGroup = groupValue ? expandedVariantGroups[groupValue] !== false : true;
              if (groupValue && !isExpandedGroup && !isFirstInGroup) {
                return null;
              }

              const variantId = v.id || v.tempId;
              const isVariantExpanded = expandedVariantId === variantId;

              return (
              <React.Fragment key={v.id || v.tempId || idx}>
                {isFirstInGroup && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ background: '#f7f7f7', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => setExpandedVariantGroups((prev) => ({ ...prev, [groupValue]: prev[groupValue] === false }))}
                    >
                      {groupValue} ({variants.filter((item) => ((getOptionValue(item, groupName) || 'Other') === groupValue)).length} variants)
                    </td>
                  </tr>
                )}
              <tr className={isVariantExpanded ? 'variantActiveRow' : ''}>
                <td className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedVariantRowIndexes.has(idx)}
                    onChange={() => toggleProductVariantRowSelection(idx)}
                  />
                </td>
                <td>
                  {(() => {
                    const { url: variantImageUrl, isAssigned } = getVariantImage(v);
                    const handleClick = (e) => {
                      e.stopPropagation();
                      setImagePickerVariant(v);
                      setShowImagePickerModal(true);
                    };
                    if (variantImageUrl && isAssigned) {
                      return (
                        <img
                          src={variantImageUrl}
                          alt={v.variant_name}
                          className="variantImg"
                          onClick={handleClick}
                        />
                      );
                    }
                    if (variantImageUrl && !isAssigned) {
                      // Not actually bound to this variant — show it faded with
                      // a clear "not assigned" affordance instead of looking
                      // identical to a real assignment.
                      return (
                        <div
                          className="variantImgUnassigned"
                          onClick={handleClick}
                          title="No image assigned to this variant yet — click to assign"
                        >
                          <img src={variantImageUrl} alt="" className="variantImgUnassignedPreview" />
                          <span className="variantImgUnassignedBadge">+</span>
                        </div>
                      );
                    }
                    return (
                      <div className="variantImgPlaceholder" onClick={handleClick}>
                        +
                      </div>
                    );
                  })()}
                </td>
                <td>
                  <span className="variantsReadOnlyName">
                    {v.variant_name || v.name || '-'}
                  </span>
                </td>
                <td>
                  <InlineCell
                    value={v.sku}
                    variantId={v.id || v.tempId}
                    field="sku"
                    onSave={(vid, f, val) => handleVariantFieldChange(vid, f, val.toUpperCase())}
                    isSaving={savingVariantField.variantId === (v.id || v.tempId) && savingVariantField.field === 'sku'}
                    error={variantErrors[v.id || v.tempId]?.sku}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td>
                  <InlineCell
                    value={v.price}
                    variantId={v.id || v.tempId}
                    field="price"
                    type="number"
                    onSave={handleVariantFieldChange}
                    isSaving={savingVariantField.variantId === (v.id || v.tempId) && savingVariantField.field === 'price'}
                    error={variantErrors[v.id || v.tempId]?.price}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td>
                  <InlineCell
                    value={v.meta?.inventory}
                    variantId={v.id || v.tempId}
                    field="inventory"
                    type="number"
                    onSave={(vid, f, val) => handleVariantFieldChange(vid, "inventory", parseInt(val) || 0)}
                    isSaving={savingVariantField.variantId === (v.id || v.tempId) && savingVariantField.field === 'inventory'}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="variantEditIconBtn"
                      onClick={() => handleToggleExpand(v)}
                      title="Edit variant details"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    {onDeleteVariant && (
                      <button
                        type="button"
                        className="variantEditIconBtn"
                        style={{ color: '#e53e3e' }}
                        onClick={() => onDeleteVariant(v.id || v.tempId)}
                        title="Delete variant"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                          <path d="M10 11v6"></path>
                          <path d="M14 11v6"></path>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {isVariantExpanded && (
                <tr className="variantExpandedRow">
                  <td colSpan={7}>
                    <div className="variantInlineForm">
                      <VariantInlineEditForm
                        variant={v}
                        onCancel={() => setExpandedVariantId(null)}
                        onUpdate={(updatedVariant) => {
                          const variantId = updatedVariant.id || updatedVariant.tempId;
                          setAllVariants((prev) =>
                            prev.map((item) => {
                              const itemId = item.id || item.tempId;
                              if (itemId !== variantId) return item;
                              return {
                                ...item,
                                variant_name: updatedVariant.variant_name || '',
                                sku: updatedVariant.sku || '',
                                price: updatedVariant.price || '',
                                compare_price: updatedVariant.compare_price || '',
                                hs_code: updatedVariant.hs_code || '',
                                gst_rate: updatedVariant.gst_rate ?? 0,
                                weight_kg: updatedVariant.weight_kg || '',
                                length_cm: updatedVariant.length_cm || '',
                                width_cm: updatedVariant.width_cm || '',
                                height_cm: updatedVariant.height_cm || '',
                                meta: {
                                  ...(item.meta || {}),
                                  inventory: updatedVariant.inventory || 0
                                }
                              };
                            })
                          );
                          setHasChanges(true);
                        }}
                        onSave={(updatedVariant) => {
                          const variantId = updatedVariant.id || updatedVariant.tempId;
                          setAllVariants((prev) =>
                            prev.map((item) => {
                              const itemId = item.id || item.tempId;
                              if (itemId !== variantId) return item;
                              return {
                                ...item,
                                variant_name: updatedVariant.variant_name || '',
                                sku: updatedVariant.sku || '',
                                price: updatedVariant.price || '',
                                compare_price: updatedVariant.compare_price || '',
                                hs_code: updatedVariant.hs_code || '',
                                gst_rate: updatedVariant.gst_rate ?? 0,
                                weight_kg: updatedVariant.weight_kg || '',
                                length_cm: updatedVariant.length_cm || '',
                                width_cm: updatedVariant.width_cm || '',
                                height_cm: updatedVariant.height_cm || '',
                                meta: {
                                  ...(item.meta || {}),
                                  inventory: updatedVariant.inventory || 0
                                }
                              };
                            })
                          );
                          setExpandedVariantId(null);
                          setHasChanges(true);
                        }}
                        setImagePickerVariant={setImagePickerVariant}
                        setShowImagePickerModal={setShowImagePickerModal}
                      />
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
      <div className="variantSummary">
        Total Inventory: {calculateTotalInventory()}
      </div>
    </div>
  );
};

const VariantInlineEditForm = ({ variant, onCancel, onSave, onUpdate, setImagePickerVariant, setShowImagePickerModal }) => {
  const [draft, setDraft] = useState({
    ...variant,
    variant_name: variant.variant_name || variant.name || '',
    inventory: variant.meta?.inventory || 0,
  });

  const handleChange = (field, value) => {
    const updated = { ...draft, [field]: value };
    setDraft(updated);
    onUpdate?.(updated);
  };

  return (
    <div className="variantEditForm">
      <div className="formGroup" style={{ gridColumn: '1 / -1' }}>
        <label>Variant image</label>
        <button
          type="button"
          className="bulkActionButton"
          onClick={() => {
            setImagePickerVariant(variant);
            setShowImagePickerModal(true);
          }}
        >
          Select image
        </button>
      </div>
      <div className="formGroup"><label>Variant name</label><input className="formInput" value={draft.variant_name || ''} onChange={(e) => handleChange('variant_name', e.target.value)} /></div>
      <div className="formGroup"><label>SKU</label><input className="formInput" value={draft.sku || ''} onChange={(e) => handleChange('sku', e.target.value.toUpperCase())} /></div>
      <div className="formGroup"><label>Price</label><input className="formInput" type="number" value={draft.price || ''} onChange={(e) => handleChange('price', e.target.value)} /></div>
      <div className="formGroup"><label>Inventory</label><input className="formInput" type="number" min="0" value={draft.inventory || 0} onChange={(e) => handleChange('inventory', e.target.value)} /></div>
      <div className="formGroup"><label>Compare at price</label><input className="formInput" type="number" value={draft.compare_price || ''} onChange={(e) => handleChange('compare_price', e.target.value)} /></div>
      <div className="formGroup"><label>HSN code</label>
        <input 
          className="formInput" 
          value={draft.hs_code || ''} 
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            handleChange('hs_code', value);
          }}
          placeholder="6109 or 61091000"
          maxLength={13}
        />
        <div className="helperText">
          Enter 4 to 8 digit HSN/SAC code (e.g. 6109, 61091000)
        </div>
      </div>
      <div className="formGroup"><label>GST (%)</label><input className="formInput" type="number" value={draft.gst_rate ?? ''} onChange={(e) => handleChange('gst_rate', e.target.value)} /></div>
      <div className="formGroup"><label>Weight (kg)</label><input className="formInput" type="number" value={draft.weight_kg || ''} onChange={(e) => handleChange('weight_kg', e.target.value)} /></div>
      <div className="formGroup"><label>Length (cm)</label><input className="formInput" type="number" value={draft.length_cm || ''} onChange={(e) => handleChange('length_cm', e.target.value)} /></div>
      <div className="formGroup"><label>Width (cm)</label><input className="formInput" type="number" value={draft.width_cm || ''} onChange={(e) => handleChange('width_cm', e.target.value)} /></div>
      <div className="formGroup"><label>Height (cm)</label><input className="formInput" type="number" value={draft.height_cm || ''} onChange={(e) => handleChange('height_cm', e.target.value)} /></div>
      
      <div className="variantEditActions">
        <button type="button" className="bulkActionButton" onClick={onCancel}>Cancel</button>
        <button type="button" className="bulkActionButton primary" onClick={() => onSave(draft)}>Save</button>
      </div>
    </div>
  );
};

const VariantDetailsModal = ({ 
  showVariantDetailModal, 
  setShowVariantDetailModal, 
  variantDetailDraft, 
  setVariantDetailDraft, 
  handleSaveVariantDetails, 
  setImagePickerVariant, 
  setShowImagePickerModal 
}) => {
  if (!showVariantDetailModal) return null;
  return (
    <div className="imagePickerModal">
      <div className="imagePickerContent" style={{ maxWidth: '760px' }}>
        <div className="imagePickerHeader">
          <h3>Variant Details</h3>
          <button type="button" onClick={() => setShowVariantDetailModal(false)} className="closeButton">×</button>
        </div>
        <div className="imagePickerBody" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="formGroup" style={{ gridColumn: '1 / -1' }}>
            <label>Variant image</label>
            <button
              type="button"
              className="bulkActionButton"
              onClick={() => {
                setImagePickerVariant(variantDetailDraft);
                setShowImagePickerModal(true);
              }}
            >
              Select image
            </button>
          </div>
          <div className="formGroup"><label>Variant name</label><input value={variantDetailDraft.variant_name || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, variant_name: e.target.value }))} /></div>
          <div className="formGroup"><label>SKU</label><input value={variantDetailDraft.sku || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, sku: e.target.value.toUpperCase() }))} /></div>
          <div className="formGroup"><label>Price</label><input type="number" value={variantDetailDraft.price || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, price: e.target.value }))} /></div>
          <div className="formGroup"><label>Inventory</label><input type="number" min="0" value={variantDetailDraft.inventory || 0} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, inventory: e.target.value }))} /></div>
          <div className="formGroup"><label>Compare at price</label><input type="number" value={variantDetailDraft.compare_price || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, compare_price: e.target.value }))} /></div>
          <div className="formGroup"><label>HSN code</label>
            <input 
              value={variantDetailDraft.hs_code || ''} 
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setVariantDetailDraft((prev) => ({ ...prev, hs_code: value }));
              }}
              placeholder="6109 or 61091000"
              maxLength={13}
            />
            <div className="helperText">
              Enter 4 to 8 digit HSN/SAC code (e.g. 6109, 61091000)
            </div>
          </div>
          <div className="formGroup"><label>GST (%)</label><input type="number" value={variantDetailDraft.gst_rate || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, gst_rate: e.target.value }))} /></div>
          <div className="formGroup"><label>Weight (kg)</label><input type="number" value={variantDetailDraft.weight_kg || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, weight_kg: e.target.value }))} /></div>
          <div className="formGroup"><label>Length (cm)</label><input type="number" value={variantDetailDraft.length_cm || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, length_cm: e.target.value }))} /></div>
          <div className="formGroup"><label>Width (cm)</label><input type="number" value={variantDetailDraft.width_cm || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, width_cm: e.target.value }))} /></div>
          <div className="formGroup"><label>Height (cm)</label><input type="number" value={variantDetailDraft.height_cm || ''} onChange={(e) => setVariantDetailDraft((prev) => ({ ...prev, height_cm: e.target.value }))} /></div>
        </div>
        <div className="imagePickerFooter">
          <button type="button" className="bulkActionButton" onClick={() => setShowVariantDetailModal(false)}>Cancel</button>
          <button type="button" className="bulkActionButton primary" onClick={handleSaveVariantDetails}>Done</button>
        </div>
      </div>
    </div>
  );
};

const ImagePickerModal = ({ 
  showImagePickerModal, 
  setShowImagePickerModal, 
  imagePickerVariant, 
  allMediaItems, 
  allVariants, 
  handleVariantImageChange 
}) => {
  if (!showImagePickerModal) return null;

  const currentVariantId = imagePickerVariant?.id || imagePickerVariant?.tempId;
  // Use variant.image_id as source of truth; fall back to media scan for unsaved variants
  const currentImageId = imagePickerVariant?.image_id
    || findMediaForVariant(allMediaItems, currentVariantId)?.id
    || null;


  const pickerItems = allMediaItems.reduce((acc, m) => {
    const existingIdx = acc.findIndex(x => x.url === m.url);
    if (existingIdx === -1) {
      acc.push(m);
    } else if (m.id === currentImageId) {
      acc[existingIdx] = m;
    }
    return acc;
  }, []);

  return (
    <div className="imagePickerModal">
      <div className="imagePickerContent">
        <div className="imagePickerHeader">
          <h3>Assign Image to Variant: {imagePickerVariant?.variant_name}</h3>
          <button type="button" onClick={() => setShowImagePickerModal(false)} className="closeButton">×</button>
        </div>
        <div className="imagePickerBody">
          <div className="imagePickerGrid">
            {pickerItems.map((item, idx) => (
              <div
                key={item.id || idx}
                className={`imagePickerItem ${currentImageId === item.id ? 'selected' : ''}`}
                onClick={() => handleVariantImageChange(currentVariantId, item.id)}
              >
                <img src={item.url} alt={item.alt} />
                {item.is_primary && <span className="primaryIndicator">Primary</span>}
                {/* One image can serve several variants, so name the OTHER variants
                    it is already on. This is contextual help while choosing, not a
                    permanent label on the gallery tile. */}
                {(() => {
                  const names = otherVariantNames(item, allVariants, currentVariantId);
                  if (!names.length) return null;
                  return (
                    <span className="imageBadge" title={names.join(', ')}>
                      {names.length === 1 ? names[0] : `${names.length} variants`}
                    </span>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
        <div className="imagePickerFooter">
          {currentImageId && (
            <button
              type="button"
              className="bulkActionButton danger"
              onClick={() => handleVariantImageChange(currentVariantId, null)}
            >
              Remove Image
            </button>
          )}
          <button
            type="button"
            className="bulkActionButton"
            onClick={() => setShowImagePickerModal(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Helper component for managing product options inline
 */
const OptionManager = ({ options, availableOptions, setFormData, generateVariants }) => {
  const [inputValue, setInputValue] = useState({});
  const [customOptionName, setCustomOptionName] = useState('');

  const addOptionRow = (name) => {
    if (!name || name === "+ Add Option") return;
    if (options.some(opt => opt.name.toLowerCase() === name.toLowerCase())) return;
    const newOptions = [...options, { name, values: [] }];
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleAddCustomOption = () => {
    const name = customOptionName.trim();
    if (!name) return;
    if (options.length >= 3) { alert('Maximum 3 options allowed'); return; }
    addOptionRow(name);
    setCustomOptionName('');
  };

  const removeOptionRow = (idx) => {
    const newOptions = options.filter((_, i) => i !== idx);
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const syncValues = (idx, values) => {
    const cleaned = values.map((value) => value.trim()).filter(Boolean);
    const newOptions = [...options];
    newOptions[idx].values = [...new Set(cleaned)];
    setFormData((prev) => ({ ...prev, options: newOptions }));
  };

  const removeValue = (optIdx, valIdx) => {
    const newOptions = [...options];
    newOptions[optIdx].values = newOptions[optIdx].values.filter((_, i) => i !== valIdx);
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const filteredAvailableOptions = availableOptions.filter(
    optName => !options.some(opt => opt.name === optName)
  );

  const showDoneButton = options.length > 0 && options.some((opt, idx) => {
    const draftValues = inputValue[idx] || [];
    return opt.values.length > 0 || draftValues.some((value) => value.trim().length > 0);
  });

  return (
    <div className="optionManager">
      {/* Add Option */}
      {options.length < 3 && (
        <div className="addOptionContainer" style={{ marginBottom: '1.5rem', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {filteredAvailableOptions.length > 0 && (
            <select
              className="formInput"
              value=""
              onChange={(e) => { if (e.target.value) { addOptionRow(e.target.value); e.target.value = ''; } }}
              style={{ maxWidth: '180px' }}
            >
              <option value="">Select option</option>
              {filteredAvailableOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            value={customOptionName}
            onChange={(e) => setCustomOptionName(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomOption(); } }}
            placeholder="Type option name (e.g. Size, Color)"
            className="formInput"
            style={{ flex: 1, minWidth: '180px' }}
          />
          <button
            type="button"
            onClick={handleAddCustomOption}
            style={{ padding: '8px 16px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            + Add Option
          </button>
        </div>
      )}

      {/* Option Rows */}
      {options.map((option, idx) => (
        <div key={idx} className="optionRowCard" style={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '1.25rem',
          marginBottom: '1.5rem',
          position: 'relative',
          backgroundColor: '#fff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <span style={{ fontWeight: 'bold', fontSize: '1rem', color: '#333' }}>{option.name}</span>
            <button
              type="button"
              onClick={() => removeOptionRow(idx)}
              style={{
                color: '#dc3545',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem'
              }}
            >
              Remove
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            <div style={{ width: '100%' }}>
              {(inputValue[idx] || ['']).map((value, inputIdx, allInputs) => (
                <input
                  key={`${idx}-${inputIdx}`}
                  type="text"
                  placeholder={inputIdx === allInputs.length - 1 ? "Add another value" : "Option value"}
                  className="formInput"
                  value={value}
                  onChange={(e) => {
                    const nextInputs = [...allInputs];
                    nextInputs[inputIdx] = e.target.value;
                    if (inputIdx === allInputs.length - 1 && e.target.value.trim()) {
                      nextInputs.push('');
                    }
                    setInputValue((prev) => ({ ...prev, [idx]: nextInputs }));
                    syncValues(idx, nextInputs);
                  }}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                />
              ))}
            </div>
          </div>

          <div className="tagsInput" style={{ border: 'none', padding: 0 }}>
            {option.values.map((val, vIdx) => (
              <span key={vIdx} className="tag">
                {val}
                <button type="button" onClick={() => removeValue(idx, vIdx)} className="removeTag">×</button>
              </span>
            ))}
          </div>
        </div>
      ))}

      {/* Done Button */}
      {showDoneButton && (
        <div style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="bulkActionButton"
            onClick={generateVariants}
            style={{ width: '100%', padding: '12px', backgroundColor: '#00bfae', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

  /**
   * Sortable Image Item Component
   */
  const SortableImage = ({ image, index, isReadOnly, onRemove, isSelected, onToggleSelect }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: image.id || image.url });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 100 : 1,
      opacity: isDragging ? 0.5 : 1,
      position: 'relative',
      cursor: isReadOnly ? 'default' : 'grab'
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="imageItem">
        {!isReadOnly && (
          <div
            className="imageCheckbox"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              zIndex: 20,
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: '4px',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(image.id || image.url)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', margin: 0 }}
            />
          </div>
        )}
        <img src={image.url} alt={image.alt || `Product image ${index + 1}`} />

        {/* First image gets "Main" badge */}
        {index === 0 && (
          <span className="primaryBadge" style={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            zIndex: 10,
            backgroundColor: '#00bfae',
            color: '#fff',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 'bold'
          }}>
            Main
          </span>
        )}

        <div className="imageControls">
          {image.is_primary && (
            <span className="primaryBadge" style={{ display: 'inline-block', padding: '2px 8px', backgroundColor: '#00bfae', color: '#fff', borderRadius: '4px', fontSize: '0.75rem', marginBottom: '0.5rem' }}>Primary</span>
          )}
        </div>
      </div>
    );
  };

/**
 * Inline Edit Cell Component to prevent cursor jumping
 */
const InlineCell = ({ value, variantId, field, type = "text", onSave, isSaving, error, setHasChanges }) => {
  const [localValue, setLocalValue] = useState(value);

  // Sync local value when external value changes (after API save)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onSave(variantId, field, localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <div className="inlineCellContainer">
      <input
        key={`variant-${field}-${variantId}`}
        type={type}
        value={localValue === null || localValue === undefined ? "" : localValue}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          setLocalValue(e.target.value);
          if (setHasChanges) setHasChanges(true);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`inlineInput ${isSaving ? 'saving' : ''} ${error ? 'error' : ''}`}
      />
      {error && <span className="fieldError small">{error}</span>}
    </div>
  );
};

// Shopify-style status dropdown: closed shows the current status, open lists all
// three catalog statuses with a check on the current one(s) and a short description,
// matching Shopify's own product-status control.
const STATUS_DROPDOWN_OPTIONS = [
  { value: 'active', label: 'Active', description: 'Sell via selected sales channels and markets' },
  { value: 'draft', label: 'Draft', description: 'Not visible on selected sales channels or markets' },
  { value: 'unlisted', label: 'Unlisted', description: 'Accessible only by direct link' },
];

const StatusDropdown = ({ currentStatus, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const currentOption = STATUS_DROPDOWN_OPTIONS.find((o) => o.value === currentStatus);

  const handleOptionClick = (value) => {
    setOpen(false);
    if (value === currentStatus) return;
    onSelect(value);
  };

  return (
    <div className="statusDropdown" ref={containerRef}>
      <button
        type="button"
        className="statusDropdownTrigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{currentOption?.label || formatStatusLabel(currentStatus)}</span>
        <span className={`statusDropdownChevron ${open ? 'open' : ''}`}>⌃</span>
      </button>
      {open && (
        <div className="statusDropdownMenu">
          {STATUS_DROPDOWN_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              className="statusDropdownOption"
              onClick={() => handleOptionClick(option.value)}
            >
              <span className="statusDropdownCheck">
                {option.value === currentStatus ? '✓' : ''}
              </span>
              <span className="statusDropdownOptionText">
                <span className="statusDropdownOptionLabel">{option.label}</span>
                <span className="statusDropdownOptionDescription">{option.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function ProductDetailView({ product, isReadOnly, onSave, onClose, highlightMissingFields = [], missingFields = [], hideSections = [], hideHsCodeField = false, hideApprovalButton = false, skipReasonModal = false, userRole: propUserRole = null }) {
  // Get user role from prop, Redux, or API
  const reduxUserRole = useSelector(state => state.user?.role || state.user?.user_type || null);
  const [userRole, setUserRole] = useState(propUserRole || reduxUserRole);
  const [exportingLogs, setExportingLogs] = useState(false);

  const isAdmin = userRole === 'admin';

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        console.log('User role from backend:', roleData);
        setUserRole(roleData.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('admin');
      }
    };
    fetchUserRole();
  }, [userRole]);

  // Determine if meta fields should be readonly (vendor role)
  const isMetaFieldsReadOnly = userRole === 'vendor';
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    tags: [],
    images: [],
    features: '',
    careInstructions: '',
    sku: '',
    variantName: '',
    inventory: 0,
    price: '',
    compareAtPrice: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    hsCode: '',
    brandCommission: '',
    gstRate: '',
    merchantSplit: '',
    vendorRemarks: '',
    metaFields: {},
    hasVariants: false,
    options: []
  });
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [variantErrors, setVariantErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestedChanges, setRequestedChanges] = useState([]);
  const [showRequestedBanner, setShowRequestedBanner] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedAuditEntries, setExpandedAuditEntries] = useState({});
  const [newImageAlt, setNewImageAlt] = useState('');
  const [showAddImageInput, setShowAddImageInput] = useState(false);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [allVariants, setAllVariants] = useState([]);
  const allVariantsRef = useRef([]);
  allVariantsRef.current = allVariants; // keep ref in sync with every render
  // pendingEditsRef accumulates every field change the user makes before Save.
  // It is NEVER overwritten by renders — only cleared when the product loads or saves.
  const pendingEditsRef = useRef({}); // { variantId: { field: value, ... } }
  const [allMediaItems, setAllMediaItems] = useState([]); // Store all media items across all variants
  // allMediaItemsRef mirrors allMediaItems synchronously (same pattern as allVariantsRef above).
  // Save is built from this ref, not the allMediaItems state closure directly: setAllMediaItems
  // inside handleVariantImageChange (the image picker) is async, and a user can click Save/Push
  // to Pending Approval before that state update has flushed — the save payload was then built
  // from a stale snapshot that didn't yet reflect the just-picked variant image assignment, so
  // the full-authoritative-snapshot save silently reverted it back to unassigned.
  const allMediaItemsRef = useRef([]);
  allMediaItemsRef.current = allMediaItems; // keep ref in sync with every render
  const [touchedMetaKeys, setTouchedMetaKeys] = useState([]); // keys user edited/added
  const [deletedMetaKeys, setDeletedMetaKeys] = useState([]); // keys user removed
  const [recentAdminChanges, setRecentAdminChanges] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type }), 5000);
  };

  // Reason modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [saveReason, setSaveReason] = useState('');
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [isPushToPendingApproval, setIsPushToPendingApproval] = useState(false);

  // Image picker modal state
  const [showImagePickerModal, setShowImagePickerModal] = useState(false);
  const [imagePickerVariant, setImagePickerVariant] = useState(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionValues, setOptionValues] = useState({});
  const [currentOptionValue, setCurrentOptionValue] = useState({});
  const [newOptionNameTab, setNewOptionNameTab] = useState('');
  const [savingVariantField, setSavingVariantField] = useState({ variantId: null, field: null });
  const [showVariantDetailModal, setShowVariantDetailModal] = useState(false);
  const [variantDetailDraft, setVariantDetailDraft] = useState(null);
  const [expandedVariantId, setExpandedVariantId] = useState(null);
  const [variantGroupBy, setVariantGroupBy] = useState('');
  const [expandedVariantGroups, setExpandedVariantGroups] = useState({});
  const [selectedVariantRowIndexes, setSelectedVariantRowIndexes] = useState(() => new Set());

  useEffect(() => {
    setSelectedVariantRowIndexes((prev) => {
      const next = new Set([...prev].filter((i) => i >= 0 && i < allVariants.length));
      if (next.size === prev.size) {
        for (const i of prev) {
          if (!next.has(i)) return next;
        }
        return prev;
      }
      return next;
    });
  }, [allVariants]);

  const toggleProductVariantRowSelection = useCallback((rowIndex) => {
    setSelectedVariantRowIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }, []);

  const handleProductVariantsHeaderSelectionClick = useCallback((e) => {
    e.stopPropagation();
    setSelectedVariantRowIndexes((prev) => {
      const total = allVariants.length;
      if (total === 0) return prev;
      let selectedCount = 0;
      for (let i = 0; i < total; i++) {
        if (prev.has(i)) selectedCount++;
      }
      if (selectedCount === total) return new Set();
      return new Set(Array.from({ length: total }, (_, idx) => idx));
    });
  }, [allVariants.length]);

  // Cancel save from modal
  const handleCancelSave = () => {
    setShowReasonModal(false);
    setPendingSaveData(null);
    setSaveReason('');
    setIsPushToPendingApproval(false);
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await listCategories();
        if (response && response.categories) {
          setCategories(response.categories);
          // Store global variant options for the dropdown
          if (response.variants_options) {
            setAvailableOptions(response.variants_options.map(opt => opt.name));
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Reset initialization flag when switching products
  useEffect(() => {
    setIsFormInitialized(false);
    setHasChanges(false);
  }, [product?.id]);

  // Initialize form data from product and detect requested changes
  useEffect(() => {
    if (!product) {
      setIsFormInitialized(false);
      return;
    }

    // Reset initialization flag while we hydrate state from the incoming product
    setIsFormInitialized(false);

    if (product) {
      console.log('[ProductDetailView] Product data:', product);
      console.log('[ProductDetailView] First variant:', product.variants?.[0]);
      console.log('[ProductDetailView] Inventory from variant meta:', product.variants?.[0]?.meta?.inventory);

      // Store all variants (clean variant meta to match product.meta - remove backend-deleted keys)
      if (product.variants && product.variants.length > 0) {
        const serverProductMeta = { ...(product.meta || {}) };
        const serverMetaKeys = new Set(Object.keys(serverProductMeta));
        const cleanedVariants = product.variants.map(v => {
          const vmeta = { ...(v.meta || {}) };
          Object.keys(vmeta).forEach(k => {
            if (['inventory', 'tags', 'vendor_remarks', 'rejection_reason'].includes(k)) return;
            if (!serverMetaKeys.has(k)) delete vmeta[k];
          });
          return { ...v, meta: vmeta };
        });
        setAllVariants(cleanedVariants);
        pendingEditsRef.current = {}; // reset when a fresh product loads
        setSelectedVariantIndex(0);

        // Use has_variants from backend if available, otherwise determine from variant count
        const hasVariants = product.has_variants !== undefined ? product.has_variants : cleanedVariants.length > 1;
        setFormData(prev => ({ ...prev, hasVariants }));
      } else {
        // Single variant or no variants - set hasVariants to false
        setFormData(prev => ({ ...prev, hasVariants: false }));
      }


      const savedOptions = getProductOptions(product);
      setSelectedOptions(savedOptions.map(opt => opt.name));
      setOptionValues(
        Object.fromEntries(savedOptions.map(opt => [opt.name, [...(opt.values || [])]]))
      );
      setCurrentOptionValue(Object.fromEntries(savedOptions.map(opt => [opt.name, ''])));

      // Store available category options if specific to category
      const selectedCategory = categories.find(cat => cat.id === product.category_id);
      if (selectedCategory?.variants_options?.length > 0) {
        setAvailableOptions(selectedCategory.variants_options.map(opt => opt.name || opt));
      }

      // Store all media items with their variant_ids
      const allMedia = (product.media || []).map((media, idx) => ({
        id: media.id,
        url: media.url,
        alt: media.alt_text || media.url,
        is_primary: media.is_primary || false,
        variant_ids: mediaVariantIds(media),
        variant_id: mediaVariantIds(media)[0] || null,
        position: media.position ?? idx,
      }));
      setAllMediaItems(allMedia);
      allMediaItemsRef.current = allMedia;

      // Map API response to form data
      const mappedData = mapApiResponseToFormData(product);

      // Merge mapped data into existing formData so we don't wipe internal flags (e.g. _needsOptionInit)
      setFormData(prev => ({ ...prev, ...mappedData }));

      // Align deletedMetaKeys with server: if variants previously had keys that product.meta no longer has, mark them deleted
      try {
        const productMetaKeys = new Set(Object.keys(product.meta || {}));
        const variantKeys = new Set();
        (product.variants || []).forEach(v => {
          Object.keys(v.meta || {}).forEach(k => variantKeys.add(k));
        });
        const deleted = [...variantKeys].filter(k => !productMetaKeys.has(k) && !['inventory', 'tags', 'vendor_remarks', 'rejection_reason'].includes(k));
        if (deleted.length > 0) setDeletedMetaKeys(prev => Array.from(new Set([...(prev || []), ...deleted])));
      } catch (e) {
        // ignore
      }

      // Detect requested changes from the most recent request_changes audit entry
      const changes = [];
      const audit = product.auditHistory || [];
      // Find the most recent request_changes entry
      let latestRequestEntry = null;
      audit.forEach(entry => {
        const action = (entry.action || '').toString().toLowerCase();
        if (action === 'request_changes' || action === 'request changes' || action === 'request-change' || action.includes('request_changes')) {
          if (!latestRequestEntry) latestRequestEntry = entry;
          else {
            const tExisting = new Date(latestRequestEntry.timestamp).getTime();
            const tNew = new Date(entry.timestamp).getTime();
            if (!Number.isNaN(tNew) && tNew > tExisting) latestRequestEntry = entry;
          }
        }
      });

      if (latestRequestEntry) {
        const entry = latestRequestEntry;
        if (entry.changes) {
          Object.entries(entry.changes).forEach(([field, change]) => {
            if (typeof change === 'object' && change.action) {
              if (field === 'media') {
                changes.push({ field: 'media', message: change.message || change.action, timestamp: entry.timestamp, user: entry.user });
              } else if (field.startsWith('meta.')) {
                const metaKey = field.substring(5);
                let message = '';
                if (change.action === 'added') message = `New meta fields added: ${metaKey}: ${change.value}`;
                else if (change.action === 'removed') message = `Meta field removed: ${metaKey}`;
                else if (change.action === 'updated') message = `${metaKey}: ${change.old} → ${change.new}`;
                changes.push({ field: metaKey, message: message, timestamp: entry.timestamp, user: entry.user });
              }
            } else {
              changes.push({ field: field, message: change.reason || `${change.from || '—'} → ${change.to || '—'}`, timestamp: entry.timestamp, user: entry.user });
            }
          });
        }
        if (entry.reason) {
          changes.push({ field: 'general', message: entry.reason, timestamp: entry.timestamp, user: entry.user });
        }
      }

      setRequestedChanges(changes);
      // Determine if there's any 'update' action after the latest 'request_changes' entry
      try {
        let latestRequestTs = 0;
        if (latestRequestEntry) {
          const t = new Date(latestRequestEntry.timestamp).getTime();
          if (!Number.isNaN(t)) latestRequestTs = t;
        }
        let hasLaterUpdate = false;
        if (latestRequestTs > 0) {
          audit.forEach(entry => {
            const action = (entry.action || '').toString().toLowerCase();
            if (action === 'update' || action === 'product_update' || action === 'edited' || action.includes('update')) {
              const t = new Date(entry.timestamp).getTime();
              if (!Number.isNaN(t) && t > latestRequestTs) hasLaterUpdate = true;
            }
          });
        }
        setShowRequestedBanner(!(latestRequestTs > 0 && hasLaterUpdate));
      } catch (e) {
        setShowRequestedBanner(true);
      }

      // Process missing_fields from API response if present
      if (missingFields && missingFields.length > 0) {
        const errors = {};
        missingFields.forEach(fieldError => {
          errors[fieldError.field] = fieldError.message;
        });
        setValidationErrors(errors);
      } else if (product.missing_fields && product.missing_fields.length > 0) {
        // Also check if missing_fields is directly on the product object
        const errors = {};
        product.missing_fields.forEach(fieldError => {
          errors[fieldError.field] = fieldError.message;
        });
        setValidationErrors(errors);
      }
      // Flag form as ready so UI doesn't get stuck on loader when required fields are cleared
      setIsFormInitialized(true);
    }
    // Keyed on product?.id (a stable primitive), NOT the `product`/`missingFields`
    // object references. Depending on `product` itself re-runs this hydration —
    // which unconditionally overwrites allMediaItems/allVariants/etc. from the
    // (possibly stale) prop — on every parent re-render that passes a new
    // `product` object, even for the SAME product. That silently discarded
    // local edits made via the image picker (setAllMediaItems) before "Save
    // changes" could read them, so a real, persisted variant-image assignment
    // never made it into the save payload. `missingFields` is read directly
    // from the closure below (with a `product.missing_fields` fallback), so it
    // doesn't need to be a dependency — it only ever changes when `product`
    // (the same object) changes anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Separate useEffect for fetching review summary - only when product.id changes
  useEffect(() => {
    if (product?.id) {
      const fetchReviewSummary = async () => {
        try {
          const resp = await getProductReviewSummary(product.id);
          if (Array.isArray(resp?.recent_admin_changes)) {
            setRecentAdminChanges(resp.recent_admin_changes);
          }
        } catch (e) {
          console.error('Error fetching review summary:', e);
        }
      };
      fetchReviewSummary();
    }
  }, [product?.id]); // Only re-run when product ID changes

  // Validation functions
  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'title':
        if (!value || value.trim() === '') {
          errors.title = 'Product title is required';
        } else if (value.length < 3) {
          errors.title = 'Product title must be at least 3 characters';
        } else if (value.length > 200) {
          errors.title = 'Product title must be less than 200 characters';
        }
        break;

      case 'sku':
        if (!value || value.trim() === '') {
          errors.sku = 'SKU is required';
        } else if (value.length < 3) {
          errors.sku = 'SKU must be at least 3 characters';
        } else if (value.length > 50) {
          errors.sku = 'SKU must be less than 50 characters';
        }
        break;

      case 'category':
        if (!value || value.trim() === '') {
          errors.category = 'Category is required';
        }
        break;

      case 'price':
        if (!value || value === '') {
          errors.price = 'Price is required';
        } else if (isNaN(value) || parseFloat(value) <= 0) {
          errors.price = 'Price must be a number greater than 0';
        } else if (parseFloat(value) > 999999.99) {
          errors.price = 'Price must be less than ₹999,999.99';
        }
        break;

      case 'inventory':
        if (value === '' || value === null || value === undefined) {
          errors.inventory = 'Inventory is required';
        } else if (!Number.isInteger(Number(value)) || Number(value) < 0) {
          errors.inventory = 'Inventory must be a non-negative integer';
        } else if (Number(value) > 999999) {
          errors.inventory = 'Inventory must be less than 1,000,000';
        }
        break;

      case 'weight':
        if (value && (isNaN(value) || parseFloat(value) < 0)) {
          errors.weight = 'Weight must be a non-negative number';
        }
        break;

      case 'dimensions':
        if (value && (isNaN(value) || parseFloat(value) < 0)) {
          errors[`dimensions.${field}`] = 'Dimension must be a non-negative number';
        }
        break;

      case 'brandCommission':
        if (value && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          errors.brandCommission = 'Brand commission must be between 0 and 100';
        }
        break;

      case 'gstRate':
        if (value === '' || value === null || value === undefined) {
          errors.gstRate = 'GST rate is required';
        } else if (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100) {
          errors.gstRate = 'GST rate must be between 0 and 100';
        }
        break;

      case 'merchantSplit':
        if (value && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          errors.merchantSplit = 'Merchant split must be between 0 and 100';
        }
        break;

      default:
        // No validation for unknown fields
        break;
    }

    return errors;
  };

  const validateImages = (images) => {
    const errors = {};

    if (!images || images.length === 0) {
      errors.images = 'At least one image is required';
      return errors;
    }

    images.forEach((image, index) => {
      if (image.file) {
        // Check file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(image.file.type)) {
          errors[`image_${index}`] = 'Only PNG and JPG images are allowed';
        }

        // Check file size (10MB = 10 * 1024 * 1024 bytes)
        const maxSize = 10 * 1024 * 1024;
        if (image.file.size > maxSize) {
          errors[`image_${index}`] = 'Image size must be less than 10MB';
        }
      }
    });

    return errors;
  };



  const validateForm = (isPushingToPending = false) => {
    const errors = {};
    const variantValidationErrors = {};

    // Basic Product Info - Updated validation rules
    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Title is required';
    } else if (formData.title.length > 500) {
      errors.title = 'Title must not exceed 500 characters';
    }
    
    if (!formData.categoryId) {
      errors.category = 'Category is required';
    }
    
    // Description validation removed entirely

    // Images
    if (!formData.images || formData.images.length === 0) {
      errors.images = 'At least one product image is required';
    }

    // Variants validation - Updated with new rules
    if (allVariants.length === 0) {
      errors.variants = 'At least one variant must exist';
    } else {
      allVariants.forEach((v, index) => {
        const identifier = v.id || v.tempId;
        const variantErrors = {};
        
        // SKU validation
        if (!v.sku || v.sku.trim() === '') {
          variantErrors.sku = 'SKU is required';
        } else {
          if (v.sku.length > 100) {
            variantErrors.sku = 'SKU must be 100 characters or less';
          }
          if (v.sku.startsWith(' ') || v.sku.endsWith(' ')) {
            variantErrors.sku = 'SKU must not have leading or trailing spaces';
          }
        }
        
        // Price validation - updated to allow 0
        if (v.price === '' || v.price === null || isNaN(v.price) || parseFloat(v.price) < 0) {
          variantErrors.price = 'Price must be 0 or greater';
        }
        
        // Compare price validation
        if (v.compare_price && (isNaN(v.compare_price) || parseFloat(v.compare_price) < 0)) {
          variantErrors.compare_price = 'Compare price must be 0 or greater';
        }
        
        // GST rate validation
        if (v.gst_rate && (isNaN(v.gst_rate) || parseFloat(v.gst_rate) < 0 || parseFloat(v.gst_rate) > 100)) {
          variantErrors.gst_rate = 'GST rate must be between 0 and 100';
        }
        
        // HSN code validation
        if (v.hs_code && v.hs_code !== '' && (!/^\d{4,8}$/.test(v.hs_code))) {
          variantErrors.hs_code = 'HSN code must be 4 to 8 digits (numbers only)';
        }
        
        // Weight validation
        // weight_kg is optional: 0/blank means "no weight" and is normalized to null on save
        // (see parseWeight), so it is not an error here. Only a non-numeric or negative
        // typed value is invalid.
        if (v.weight_kg !== null && v.weight_kg !== undefined && v.weight_kg !== '' && (isNaN(v.weight_kg) || parseFloat(v.weight_kg) < 0)) {
          variantErrors.weight_kg = 'Weight must be a non-negative number';
        }
        
        if (Object.keys(variantErrors).length > 0) {
          variantValidationErrors[identifier] = variantErrors;
        }
      });
    }
    
    setValidationErrors(errors);
    setVariantErrors(variantValidationErrors);
    
    const totalErrors = Object.keys(errors).length + Object.keys(variantValidationErrors).length;
    
    if (totalErrors > 0) {
      setSaveStatus({
        type: 'error',
        message: `Please fix ${totalErrors} error${totalErrors > 1 ? 's' : ''} before saving.`
      });
    }
    
    return totalErrors === 0;
  };

  // Parse backend 400 error details and map to specific fields
  const handleBackendValidationErrors = (error) => {
    if (error?.status === 400 && error?.details && Array.isArray(error.details)) {
      const errors = {};
      const variantValidationErrors = {};

      error.details.forEach(detail => {
        // Parse variants[0].field_name pattern
        const variantMatch = detail.match(/variants\[(\d+)\]\.(.+)/);
        if (variantMatch) {
          const variantIndex = parseInt(variantMatch[1]);
          const fieldName = variantMatch[2];
          
          // Find the variant by index to get its ID
          const variant = allVariants[variantIndex];
          if (variant) {
            const variantId = variant.id || variant.tempId;
            
            if (!variantValidationErrors[variantId]) {
              variantValidationErrors[variantId] = {};
            }
            
            // Map backend field names to our validation messages
            let errorMessage = detail;
            if (fieldName === 'hs_code') {
              errorMessage = 'HSN code must be 4 to 8 digits (numbers only)';
            } else if (fieldName === 'price') {
              errorMessage = 'Price must be 0 or greater';
            } else if (fieldName === 'compare_price') {
              errorMessage = 'Compare price must be 0 or greater';
            } else if (fieldName === 'gst_rate') {
              errorMessage = 'GST rate must be between 0 and 100';
            } else if (fieldName === 'weight_kg') {
              errorMessage = 'Weight must be greater than 0';
            } else if (fieldName === 'sku') {
              if (detail.includes('required')) {
                errorMessage = 'SKU is required';
              } else if (detail.includes('characters')) {
                errorMessage = 'SKU must be 100 characters or less';
              } else if (detail.includes('spaces')) {
                errorMessage = 'SKU must not have leading or trailing spaces';
              }
            }
            
            variantValidationErrors[variantId][fieldName] = errorMessage;
          }
        } else {
          // Handle product-level errors
          let fieldName = detail.split(' ')[0].toLowerCase();
          if (fieldName === 'title') {
            errors.title = detail;
          } else if (fieldName === 'description') {
            errors.description = detail;
          } else if (fieldName.includes('category')) {
            errors.category = detail;
          }
        }
      });

      setValidationErrors(errors);
      setVariantErrors(variantValidationErrors);
      
      const totalErrors = Object.keys(errors).length + Object.keys(variantValidationErrors).length;
      setSaveStatus({
        type: 'error',
        message: `Please fix ${totalErrors} error${totalErrors > 1 ? 's' : ''} before saving.`
      });
      
      return true;
    }
    return false;
  };

  // Check if a field has requested changes
  const hasRequestedChanges = (field) => {
    return requestedChanges.some(change =>
      change.field === field ||
      change.field === 'general' ||
      (field === 'images' && change.field === 'media') ||
      (field === 'description' && change.field === 'description')
    );
  };

  // Handle variant selection change
  const handleVariantChange = (variantIndex) => {
    if (variantIndex < 0 || variantIndex >= allVariants.length) return;

    setSelectedVariantIndex(variantIndex);
    const selectedVariant = allVariants[variantIndex];

    // Update formData with selected variant data
    setFormData(prev => ({
      ...prev,
      variantId: selectedVariant.id || '',
      variantVersion: selectedVariant.version || 1,
      sku: selectedVariant.sku || '',
      variantName: selectedVariant.variant_name || selectedVariant.name || '',
      price: selectedVariant.price || '',
      compareAtPrice: selectedVariant.compare_price || '',
      inventory: selectedVariant.meta?.inventory || 0,
      weight: selectedVariant.weight_kg || '',
      dimensions: {
        length: selectedVariant.length_cm || '',
        width: selectedVariant.width_cm || '',
        height: selectedVariant.height_cm || ''
      },
      hsCode: selectedVariant.hs_code || '',
      brandCommission: selectedVariant.commission_pct || '',
      gstRate: selectedVariant.gst_rate ?? '',
      shopifyVariantId: selectedVariant.shopify_variant_id || null,
      // Product Images section shows all media; variant_id assignments are shown as badges
      images: allMediaItems
    }));
  };

  const handleInputChange = (field, value) => {
    // Special handling for category - update both categoryId and category name
    if (field === 'category' || field === 'categoryId') {
      const selectedCategory = categories.find(cat => cat.id === value || cat.name === value || cat.slug === value);
      setFormData(prev => ({
        ...prev,
        categoryId: selectedCategory?.id || value,
        category: selectedCategory?.name || selectedCategory?.slug || value,
        categoryName: selectedCategory?.name || value
      }));


      if (selectedCategory?.variants_options?.length > 0) {
        setAvailableOptions(selectedCategory.variants_options.map(opt => opt.name || opt));
      }
    } else if (field === 'hasVariants') {
      setFormData(prev => ({
        ...prev,
        hasVariants: value
      }));

      if (!value && allVariants.length > 1) {
        if (!window.confirm('Disabling variants will keep only the first variant. Other variants will be removed. Continue?')) {
          return;
        }
        // Keep only first variant
        setAllVariants([allVariants[0]]);
        setSelectedVariantIndex(0);
      } else if (value && allVariants.length === 0) {
        // If enabling variants and no variants exist, create default variant
        const newVariant = {
          id: null,
          sku: formData.sku || '',
          variant_name: '',
          price: formData.price || '',
          compare_price: formData.compareAtPrice || '',
          weight_kg: formData.weight || '',
          length_cm: formData.dimensions?.length || '',
          width_cm: formData.dimensions?.width || '',
          height_cm: formData.dimensions?.height || '',
          hs_code: formData.hsCode || '',
          commission_pct: formData.brandCommission || '',
          meta: {
            inventory: parseInt(formData.inventory) || 0
          }
        };
        setAllVariants([newVariant]);
        setSelectedVariantIndex(0);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));

      // If editing a variant, also update allVariants to preserve changes when switching variants
      if (selectedVariantIndex >= 0 && allVariants.length > selectedVariantIndex) {
        const variantFields = ['sku', 'variantName', 'price', 'compareAtPrice', 'inventory', 'weight', 'hsCode', 'brandCommission'];
        if (variantFields.includes(field) || field.startsWith('dimensions.')) {
          const updatedVariants = [...allVariants];
          const variant = updatedVariants[selectedVariantIndex];

          if (field === 'compareAtPrice') {
            variant.compare_price = value;
          } else if (field === 'variantName') {
            variant.variant_name = value;
          } else if (field === 'weight') {
            variant.weight_kg = value;
          } else if (field === 'hsCode') {
            variant.hs_code = value;
          } else if (field === 'brandCommission') {
            variant.commission_pct = value;
          } else if (field === 'inventory') {
            variant.meta = { ...(variant.meta || {}), inventory: parseInt(value) || 0 };
          } else if (field.startsWith('dimensions.')) {
            const dimField = field.split('.')[1];
            if (dimField === 'length') variant.length_cm = value;
            else if (dimField === 'width') variant.width_cm = value;
            else if (dimField === 'height') variant.height_cm = value;
          } else {
            variant[field] = value;
          }

          setAllVariants(updatedVariants);
        }
      }
    }

    setHasChanges(true);

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleArrayChange = (field, index, value) => {
    if (field === 'images') {
      // When updating an image, also update it in allMediaItems
      const updatedImage = { ...value };
      // Preserve variant_id if not explicitly provided
      if (!updatedImage.variant_id && formData.images[index]?.variant_id) {
        updatedImage.variant_id = formData.images[index].variant_id;
      }

      // Update in formData
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].map((item, i) => i === index ? updatedImage : item)
      }));

      // Update in allMediaItems
      const imageToUpdate = formData.images[index];
      setAllMediaItems(prev => {
        const next = prev.map(img => {
          // Match by id if available, otherwise match by url
          if (imageToUpdate.id && img.id === imageToUpdate.id) {
            return updatedImage;
          } else if (!imageToUpdate.id && img.url === imageToUpdate.url &&
            img.variant_id === imageToUpdate.variant_id) {
            return updatedImage;
          }
          return img;
        });
        allMediaItemsRef.current = next;
        return next;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].map((item, i) => i === index ? value : item)
      }));
    }
    setHasChanges(true);
  };

  const handleAddToArray = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem]
    }));
    setHasChanges(true);
  };

  const handleRemoveFromArray = (field, index) => {
    if (field === 'images') {
      // When removing an image, also remove it from allMediaItems
      const imageToRemove = formData.images[index];
      setAllMediaItems(prev => {
        const next = prev.filter(img => {
          // If image has an ID, match by ID
          if (imageToRemove.id && img.id) {
            return img.id !== imageToRemove.id;
          }
          // Otherwise match by URL and variant_id
          return !(img.url === imageToRemove.url && img.variant_id === imageToRemove.variant_id);
        });
        allMediaItemsRef.current = next;
        return next;
      });

      // Also remove from selectedMediaIds if present
      const imageId = imageToRemove.id || imageToRemove.url;
      setSelectedMediaIds(prev => prev.filter(id => id !== imageId));
    }
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };


  // Handle image file upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication required. Please log in.');
      return;
    }

    setUploadingImage(true);
    setImageUploadError('');

    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB as per backend

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          throw new Error(`${file.name}: Unsupported file type. Please upload JPG, PNG, GIF, or WebP images.`);
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
          throw new Error(`${file.name}: File size exceeds 25MB limit.`);
        }

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('file_type', 'image');

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/core/s3/upload/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || `Failed to upload ${file.name}`);
        }

        return res.json();
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Get variant ID if a variant is selected — use tempId for new variants
      const selectedVariant = selectedVariantIndex >= 0 ? allVariants[selectedVariantIndex] : null;
      const variantId = selectedVariant
        ? (selectedVariant.id || selectedVariant.tempId || null)
        : formData.variantId || null;

      // Use the provided alt text (if any) for all uploaded files; fall back to filename
      const providedAlt = (newImageAlt || '').trim();

      // Add uploaded images to the images array
      uploadedFiles.forEach((upload, index) => {
        const newImage = {
          id: `temp-${Date.now()}-${Math.random()}`,
          url: upload.file_url,
          alt: providedAlt || upload.filename || '',
          is_primary: (formData.images && formData.images.length === 0) && index === 0, // First image is primary if no images exist
          variant_ids: variantId ? [variantId] : [],
          variant_id: variantId
        };

        handleAddToArray('images', newImage);
        setAllMediaItems(prev => {
          const next = [...prev, newImage];
          allMediaItemsRef.current = next;
          return next;
        });
      });

      // Clear alt input after upload
      setNewImageAlt('');

      setShowAddImageInput(false);

    } catch (error) {
      console.error('Image upload failed:', error);
      setImageUploadError(error.message || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImage(false);
      // Clear the input so the same file can be selected again
      e.target.value = '';
    }
  };

  // We no longer support adding images by URL. Image files are uploaded via file input
  // and added to formData.images inside handleImageUpload. The alt text field
  // is still available and applied per-upload in handleImageUpload.

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm(false)) {
      showToast('Please fix validation errors before saving', 'error');
      return;
    }

    // Prepare data and show reason modal
    await prepareSaveData(false);
  };

  const handlePushToPendingApproval = async () => {
    // Validate form before pushing
    if (!validateForm(true)) {
      if (validationErrors.unsaved) {
        showToast(validationErrors.unsaved, 'error');
      } else {
        showToast('Please fix validation errors before pushing to pending approval', 'error');
      }
      return;
    }

    // Prepare data and save automatically (skip reason modal)
    await prepareSaveData(true, true);
  };

  const prepareSaveData = async (pushToPending, autoSave = false) => {
    // Read from the ref, not the allMediaItems state closure — a variant image
    // assignment (handleVariantImageChange) can still be mid-flight or have just
    // resolved when Save is clicked, and setAllMediaItems' state update may not
    // have flushed into this closure yet. allMediaItemsRef is written synchronously
    // on every media change, so it's always current at the moment Save runs.
    const currentMediaItems = allMediaItemsRef.current;

    // Validate that no blob URLs are being saved - these are local-only URLs that won't work on Shopify
    if (hasBlobUrls(currentMediaItems)) {
      showToast('Some images failed to upload properly and cannot be saved. Please remove the failed images and re-upload them.', 'error');
      setImageUploadError('Some images have invalid URLs (blob:). Please remove and re-upload them.');
      return;
    }

    // Merge any pending field edits (typed by user) into the variants snapshot.
    // pendingEditsRef is written synchronously in handleVariantFieldChange and is
    // never overwritten by re-renders, so it always has the latest typed values.
    const pending = pendingEditsRef.current;
    console.log('[SAVE] pendingEditsRef at save time:', JSON.stringify(pending));
    let finalVariants = allVariantsRef.current.map(v => {
      const vid = v.id || v.tempId;
      const edits = pending[vid];
      if (!edits) return v;
      const { meta: metaEdits, ...scalarEdits } = edits;
      return {
        ...v,
        ...scalarEdits,
        ...(metaEdits ? { meta: { ...(v.meta || {}), ...metaEdits } } : {}),
      };
    });
    console.log('[SAVE] finalVariants SKUs:', finalVariants.map(v => ({ id: v.id || v.tempId, sku: v.sku })));

    // Create updated formData with all variants and all media items
    const formDataWithAllVariants = {
      ...formData,
      allVariants: finalVariants.length > 0 ? finalVariants : [{
        id: formData.variantId || null,
        sku: formData.sku || '',
        variant_name: formData.variantName || '',
        price: formData.price || '',
        compare_price: formData.compareAtPrice || null,
        weight_kg: formData.weight || null,
        length_cm: formData.dimensions?.length || null,
        width_cm: formData.dimensions?.width || null,
        height_cm: formData.dimensions?.height || null,
        hs_code: formData.hsCode || null,
        commission_pct: formData.brandCommission || null,
        meta: {
          inventory: parseInt(formData.inventory) || 0
        }
      }],
      // Include allMediaItems so mapFormDataToApiPayload can access it
      // Ensure variant-image assignments are preserved by including allMediaItems with variant_id
      // normalizeMediaItem keeps variant_ids intact — spreading only the scalar here
      // collapsed a shared image back to a single variant on every save.
      allMediaItems: currentMediaItems && currentMediaItems.length > 0
        ? currentMediaItems.map(media => normalizeMediaItem(media))
        : (formData.images || []).map(img => normalizeMediaItem(img)),
      // Ensure product-level metaFields includes any non-inventory meta edited on the selected variant
      metaFields: (() => {
        const baseMeta = { ...(formData.metaFields || {}) };
        try {
          if (selectedVariantIndex >= 0 && finalVariants[selectedVariantIndex] && finalVariants[selectedVariantIndex].meta) {
            const variantMeta = finalVariants[selectedVariantIndex].meta || {};

            Object.entries(variantMeta).forEach(([k, v]) => {
              if (k === 'inventory') return; // skip inventory
              // Skip keys the user explicitly deleted locally
              if (deletedMetaKeys.includes(k)) return;
              // Only include if it's not a standard backend field that should only be at product level
              if (!['tags', 'vendor_remarks', 'rejection_reason'].includes(k)) {
                baseMeta[k] = v;
              }
            });
          }
        } catch (e) {
          // ignore errors in meta field merging
        }
        return baseMeta;
      })(),
      // Add flag to indicate this is a push to pending approval. `status` here always
      // stays the real terminal_status value the user chose — the backend already
      // moves the product to pending approval on any edit (see ProductService.update),
      // so there's nothing further to encode: overwriting it with a marker string like
      // 'pending_approval' would corrupt terminal_status, which only accepts real
      // catalog values (draft/active/unlisted/archived/deleted).
      ...(pushToPending && { _pushToPendingApproval: true })
    };

    // Store the data and show reason modal
    setPendingSaveData(formDataWithAllVariants);
    setIsPushToPendingApproval(pushToPending);
    setSaveReason(pushToPending ? 'Product pushed to pending approval with updates' : '');

    // If skipReasonModal or autoSave is true, save immediately without showing the modal
    if (skipReasonModal || autoSave) {
      setIsSubmitting(true);
      setSaving(true);
      try {
        await onSave(formDataWithAllVariants, autoSave && pushToPending ? 'Product pushed to pending approval' : 'Variant updated');
        pendingEditsRef.current = {}; // clear after successful save
        setHasChanges(false);
        setValidationErrors({});
        showToast('Changes saved successfully', 'success');
        // If it was a push to pending, we close the view
        if (pushToPending) {
          onClose();
        }
      } catch (error) {
        console.error('[ProductDetailView] Save error:', error);
        
        // Try to handle backend validation errors first
        if (handleBackendValidationErrors(error)) {
          return; // Error already handled by the validation error handler
        }
        
        showToast(`Error saving changes: ${error.message || 'Unknown error'}`, 'error');
      } finally {
        setIsSubmitting(false);
        setSaving(false);
      }
    } else {
      setShowReasonModal(true);
    }
  };

  const handleConfirmSave = async () => {
    if (!saveReason || saveReason.trim() === '') {
      alert('A reason is required to save changes');
      return;
    }

    setIsSubmitting(true);
    setSaving(true);
    setShowReasonModal(false);

    try {
      await onSave(pendingSaveData, saveReason.trim());
      pendingEditsRef.current = {}; // clear after successful save
      setHasChanges(false);
      setValidationErrors({});
      
      if (isPushToPendingApproval) {
        showToast('Product saved and sent for approval', 'success');
        // Give time for the toast to be seen before closing
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error saving product:', error);
      
      // Try to handle backend validation errors first
      if (handleBackendValidationErrors(error)) {
        return; // Error already handled by the validation error handler
      }
      
      alert(`Error: ${error.message || 'Failed to save product'}`);
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  /**
   * Update Product Options local state
   */
  const handleOptionChange = (updatedOptions) => {
    setFormData(prev => ({ ...prev, options: updatedOptions }));
    setHasChanges(true);
  };

  /**
   * Sync Product Options with API (PUT)
   */
  const saveOptionsToAPI = async (optionsToSave) => {
    const options = optionsToSave || formData.options || [];

    // Filter out completely empty rows and validate names
    const validOptions = options.filter(opt => opt.name.trim() !== "" || opt.values.length > 0);

    // Check if any row has values but no name
    const invalidOption = validOptions.find(opt => opt.name.trim() === "");
    if (invalidOption) {
      alert("Option name is required for all options with values.");
      return;
    }

    if (validOptions.length === 0 && options.length > 0) {
      // If we only have empty rows, don't sync yet
      return;
    }

    try {
      const payload = {
        options: validOptions,
        reason: "Updated product options"
      };

      await updateProductOptions(product.id, payload);
      console.log("[ProductDetailView] Options synced with server successfully");

      // Update local state with whatever the server returned (if we had the response)
      // Usually the server returns the full product or updated options.
      // For now, we trust our local state if success.
    } catch (error) {
      console.error("[ProductDetailView] Error syncing options:", error);
      alert("Failed to sync options: " + error.message);
    }
  };

  /**
   * Handle immediate update for a single variant field (PATCH)
   */
  const handleVariantFieldChange = async (variantId, field, value) => {
    // Determine if this is a new unsaved variant (has no real ID or starts with "new-")
    const isNew = !variantId || (typeof variantId === 'string' && variantId.startsWith('new-'));

    // Record the edit in pendingEditsRef — this is never reset by re-renders
    if (!pendingEditsRef.current[variantId]) pendingEditsRef.current[variantId] = {};
    if (field === 'inventory') {
      if (!pendingEditsRef.current[variantId].meta) pendingEditsRef.current[variantId].meta = {};
      pendingEditsRef.current[variantId].meta.inventory = value;
    } else {
      pendingEditsRef.current[variantId][field] = value;
    }

    // Optimistic update — compute eagerly so ref is updated before setAllVariants schedules a re-render
    const updatedVariants = allVariantsRef.current.map(v => {
      const vIdentifier = v.id || v.tempId;
      if (vIdentifier === variantId) {
        if (field === 'inventory') {
          return { ...v, meta: { ...(v.meta || {}), inventory: value } };
        }
        return { ...v, [field]: value };
      }
      return v;
    });
    allVariantsRef.current = updatedVariants;
    setAllVariants(updatedVariants);

    setHasChanges(true); // Bug 2 fix: Enable "Save Changes" button

    // If it's a new unsaved variant, we only update locally and return
    if (isNew) {
      console.log(`[ProductDetailView] Local-only update for new variant: ${field}=${value}`);
      return;
    }

    setSavingVariantField({ variantId, field });

    try {
      const payload = {
        [field]: value,
        reason: `Updated ${field} for variant ${variantId}`
      };
      
      await updateVariantField(product.id, variantId, payload);
      console.log(`[ProductDetailView] Variant ${variantId} field ${field} updated successfully`);
      showToast('Field updated successfully', 'success');
    } catch (error) {
      console.error(`[ProductDetailView] Error updating variant ${variantId} field ${field}:`, error);
      showToast(`Failed to update ${field}: ` + error.message, 'error');
    } finally {
      setSavingVariantField({ variantId: null, field: null });
    }
  };

  const handleDeleteVariant = (variantId) => {
    const isNew = !variantId || (typeof variantId === 'string' && variantId.startsWith('new-'));
    const confirmed = window.confirm('Are you sure you want to delete this variant? This cannot be undone.');
    if (!confirmed) return;

    if (isNew) {
      // New unsaved variant — just remove from state
      delete pendingEditsRef.current[variantId];
      const updated = allVariantsRef.current.filter(v => (v.id || v.tempId) !== variantId);
      allVariantsRef.current = updated;
      setAllVariants(updated);
    } else {
      // Existing variant — mark with _delete flag; backend will delete on Save Changes
      delete pendingEditsRef.current[variantId];
      const updated = allVariantsRef.current.map(v =>
        (v.id || v.tempId) === variantId ? { ...v, _delete: true } : v
      );
      allVariantsRef.current = updated;
      setAllVariants(updated);
    }
    setHasChanges(true);
  };

  /**
   * Inline Edit Cell Component to prevent cursor jumping
   */
  const InlineCell = ({ value, variantId, field, type = "text", onSave, isSaving, error }) => {
    const [localValue, setLocalValue] = useState(value);

    // Sync local value when external value changes (after API save)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleBlur = () => {
      if (localValue !== value) {
        onSave(variantId, field, localValue);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.target.blur();
      }
    };

    return (
      <div className="inlineCellContainer">
        <input
          key={`variant-${field}-${variantId}`}
          type={type}
          value={localValue === null || localValue === undefined ? "" : localValue}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            setLocalValue(e.target.value);
            setHasChanges(true);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`inlineInput ${isSaving ? 'saving' : ''} ${error ? 'error' : ''}`}
        />
        {error && <span className="fieldError small">{error}</span>}
      </div>
    );
  };

  /**
   * Assign an image to a variant — LOCAL STATE ONLY, nothing is persisted here.
   *
   * This used to POST to the assign-variant-image endpoint immediately, which made
   * the assignment live before the user had pushed anything for approval, and left
   * Cancel unable to undo it (there was nothing local to throw away — the change was
   * already in the database). The assignment is now carried in allMediaItems as
   * `variant_id` and written by the normal Save / Push to Pending Approval flow,
   * which mapFormDataToApiPayload already sends per media item and the backend
   * applies via set_media_variant. Cancel therefore reverts by simply discarding
   * local state, and the backend snapshots the pre-change media on that save so a
   * later rejection can revert it.
   *
   * Uses variant.image_id as display source of truth.
   */
  const handleVariantImageChange = (variantId, imageId) => {
    const isNewVariant = !variantId || (typeof variantId === 'string' && variantId.startsWith('new-'));

    setShowImagePickerModal(false);
    setImagePickerVariant(null);

    // Read from the ref, not the allMediaItems closure — it's kept synchronously
    // current (see allMediaItemsRef declaration) so back-to-back assignments (or
    // an assignment immediately followed by Save) never compute against a stale
    // snapshot from before a prior setAllMediaItems has flushed.
    const previousMediaItems = allMediaItemsRef.current;

    // A variant shows ONE image, but an image can be shown by MANY variants. So this
    // detaches `variantId` from whatever it held and attaches it to the chosen image —
    // it must NOT disturb the other variants already using that image. Modelling the
    // link as a scalar `variant_id` per media row could only ever record one of them,
    // so assigning a shared image to a second variant silently unassigned the first.
    // `variant_ids` is the real shape; `variant_id` is kept in sync as a legacy mirror.
    const nextMedia = assignVariantToMedia(previousMediaItems, variantId, imageId);
    setAllMediaItems(nextMedia);
    allMediaItemsRef.current = nextMedia;
    setAllVariants(prev => prev.map(v => {
      const vid = v.id || v.tempId;
      // Only the variant being assigned changes. Others keep their image even when it
      // is the same picture — that is the whole point of a shared image.
      if (vid === variantId) return { ...v, image_id: imageId || null };
      return v;
    }));
    setFormData(prev => ({ ...prev, images: nextMedia }));
    setHasChanges(true);

    showToast(
      isNewVariant
        ? 'Image assigned successfully'
        : 'Image assignment staged — save or push for approval to apply it',
      'success'
    );
  };


  const toggleImageSelection = (id) => {
    setSelectedMediaIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleBulkRemoveImages = () => {
    if (selectedMediaIds.length === 0) return;

    if (window.confirm(`Are you sure you want to remove ${selectedMediaIds.length} selected image(s)?`)) {
      // Get IDs to remove
      const idsToRemove = new Set(selectedMediaIds);

      // Update formData.images
      const newImages = formData.images.filter(img => !idsToRemove.has(img.id || img.url));
      setFormData(prev => ({ ...prev, images: newImages }));

      // Update allMediaItems
      setAllMediaItems(prev => {
        const next = prev.filter(img => !idsToRemove.has(img.id || img.url));
        allMediaItemsRef.current = next;
        return next;
      });

      // Clear selection and mark changes
      setSelectedMediaIds([]);
      setHasChanges(true);
      console.log(`[ProductDetailView] Bulk removed ${idsToRemove.size} images`);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formData.images.findIndex((img) => (img.id || img.url) === active.id);
      const newIndex = formData.images.findIndex((img) => (img.id || img.url) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newImages = arrayMove(formData.images, oldIndex, newIndex);

        // Update local state
        setFormData(prev => ({ ...prev, images: newImages }));
        setAllMediaItems(prev => {
          // Create a map of updated images for quick lookup
          const imageMap = new Map(newImages.map((img, idx) => [img.id || img.url, idx]));

          // Reorder allMediaItems based on the new order in formData.images
          // Images NOT in formData.images (if any) will stay at the end
          const updated = [...prev].sort((a, b) => {
            const idxA = imageMap.has(a.id || a.url) ? imageMap.get(a.id || a.url) : 999;
            const idxB = imageMap.has(b.id || b.url) ? imageMap.get(b.id || b.url) : 999;
            return idxA - idxB;
          });
          // Update position property to match new array order so subsequent saves send correct positions
          const next = updated.map((item, idx) => ({ ...item, position: idx }));
          allMediaItemsRef.current = next;
          return next;
        });

        try {
          const payload = {
            media: newImages
              .filter(img => img.id)
              .map((img, index) => ({
                id: img.id,
                position: index
              }))
          };

          if (payload.media.length > 0) {
            await reorderProductImages(product.id, payload);
            console.log("[ProductDetailView] Images reordered successfully");
            setHasChanges(true);
          }
        } catch (error) {
          console.error("[ProductDetailView] Error reordering images:", error);
          showToast("Failed to reorder images: " + error.message, 'error');
        }
      }
    }
  };

  /**
   * Generate variants from options
   */
  const generateVariants = () => {
    const options = formData.options || [];

    // Bug 2: Check for options without values
    const emptyOptions = options.filter(opt => !opt.values || opt.values.length === 0);
    if (emptyOptions.length > 0) {
      showToast(`Please add at least one value for: ${emptyOptions.map(o => o.name).join(', ')} before generating variants.`, 'error');
      return;
    }

    if (options.length === 0) {
      showToast("Please add at least one option with values to generate variants.", 'error');
      return;
    }

    // Cartesian product helper
    const getCartesianProduct = (sets) => {
      return sets.reduce((acc, set) => {
        return acc.flatMap(a => set.map(b => [...a, b]));
      }, [[]]);
    };

    const combinations = getCartesianProduct(options.map(opt => opt.values));

    // Start with a copy of current variants to preserve them
    const updatedVariants = [...allVariants];
    let addedCount = 0;

    combinations.forEach(combo => {
      const variantName = combo.join(' / ');

      // Check if this combination already exists in our local list
      const existing = updatedVariants.find(v => v.variant_name === variantName);

      if (!existing) {
        // This is a new combination, create a new variant object
        const optionValues = buildOptionValuesFromOptionsAndCombo(options, combo);

        updatedVariants.push({
          id: null,
          tempId: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          sku: '',
          variant_name: variantName,
          price: formData.price || 0,
          compare_price: formData.compareAtPrice || 0,
          hs_code: formData.hsCode || '',
          gst_rate: formData.gstRate || 0,
          meta: {
            inventory: 0,
            option_values: optionValues
          },
          option_values: optionValues
        });
        addedCount++;
      } else {
        // If it exists, ensure its option_values are synced with the current options schema
        const optionValues = buildOptionValuesFromOptionsAndCombo(options, combo);
        existing.option_values = optionValues;
        if (!existing.meta) existing.meta = {};
        existing.meta.option_values = optionValues;
      }
    });
    setAllVariants(updatedVariants);
    setHasChanges(true);
    showToast(`Generated variants. ${addedCount} new variants added.`, 'success');
  };

  const handleSaveVariantDetails = () => {
    if (!variantDetailDraft) return;
    const variantId = variantDetailDraft.id || variantDetailDraft.tempId;
    setAllVariants((prev) =>
      prev.map((item) => {
        const itemId = item.id || item.tempId;
        if (itemId !== variantId) return item;
        return {
          ...item,
          // Preserve all existing properties including image assignments
          variant_name: variantDetailDraft.variant_name || '',
          sku: variantDetailDraft.sku || '',
          price: variantDetailDraft.price || '',
          compare_price: variantDetailDraft.compare_price || '',
          hs_code: variantDetailDraft.hs_code || '',
          gst_rate: variantDetailDraft.gst_rate ?? 0,
          weight_kg: variantDetailDraft.weight_kg || '',
          length_cm: variantDetailDraft.length_cm || '',
          width_cm: variantDetailDraft.width_cm || '',
          height_cm: variantDetailDraft.height_cm || '',
          meta: {
            ...(item.meta || {}),
            inventory: variantDetailDraft.inventory || 0
          }
        };
      })
    );
    setShowVariantDetailModal(false);
    setVariantDetailDraft(null);
    setHasChanges(true);
  };

  const handleAddOption = (optionName) => {
    if (selectedOptions.length >= 3) { alert('Maximum 3 options allowed'); return; }
    if (!selectedOptions.includes(optionName)) {
      setSelectedOptions(prev => [...prev, optionName]);
      setOptionValues(prev => ({ ...prev, [optionName]: [] }));
      setCurrentOptionValue(prev => ({ ...prev, [optionName]: '' }));
    }
  };

  const handleRemoveOption = (optionName) => {
    setSelectedOptions(prev => prev.filter(id => id !== optionName));
    setOptionValues(prev => { const n = { ...prev }; delete n[optionName]; return n; });
    setCurrentOptionValue(prev => { const n = { ...prev }; delete n[optionName]; return n; });
  };

  const handleAddOptionValue = (optionName, value) => {
    if (!value || !value.trim()) return;
    setOptionValues(prev => ({ ...prev, [optionName]: [...(prev[optionName] || []), value.trim()] }));
    setCurrentOptionValue(prev => ({ ...prev, [optionName]: '' }));
  };

  const handleRemoveOptionValue = (optionName, valueIndex) => {
    setOptionValues(prev => ({ ...prev, [optionName]: prev[optionName].filter((_, i) => i !== valueIndex) }));
  };

  const handleOptionValueKeyPress = (e, optionName) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddOptionValue(optionName, currentOptionValue[optionName]); }
  };

  const hasNewOptionValues = () => selectedOptions.some(optionName => (optionValues[optionName] || []).length > 0);

  const handleGenerateVariantsFromOptions = () => {
    if (selectedOptions.length === 0) { alert('Please add at least one option with values'); return; }
    if (!hasNewOptionValues()) { alert('Add values to options above to generate variants'); return; }
    const optionsArray = selectedOptions.map(optionName => ({
      name: optionName,
      values: optionValues[optionName] || []
    }));
    // Update formData.options then compute cartesian product directly
    setFormData(prev => ({ ...prev, options: optionsArray }));
    const getCartesian = (sets) => sets.reduce((acc, set) => acc.flatMap(a => set.map(b => [...a, b])), [[]]);
    const combinations = getCartesian(optionsArray.map(o => o.values));
    const buildOptionVals = (opts, combo) => {
      const result = {};
      opts.forEach((opt, i) => { result[opt.name] = combo[i]; });
      return result;
    };
    setAllVariants(prev => {
      const updated = [...prev];
      combinations.forEach(combo => {
        const variantName = combo.join(' / ');
        if (!updated.find(v => v.variant_name === variantName)) {
          updated.push({
            id: null,
            tempId: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            sku: '',
            variant_name: variantName,
            price: formData.price || 0,
            compare_price: formData.compareAtPrice || 0,
            hs_code: formData.hsCode || '',
            gst_rate: formData.gstRate || 0,
            meta: { inventory: 0, option_values: buildOptionVals(optionsArray, combo) },
            option_values: buildOptionVals(optionsArray, combo),
          });
        }
      });
      return updated;
    });
    setHasChanges(true);
    showToast('Variants generated successfully', 'success');
  };

  const renderInventoryPricingTab = () => (
    <div>
      {!isReadOnly && (
        <div className="formGroup" style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.hasVariants || false}
              onChange={(e) => handleInputChange('hasVariants', e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            <span>This product has multiple variants</span>
          </label>
        </div>
      )}
      {formData.hasVariants && (
        <div className="formSection" style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>Product Options</h4>
            {selectedOptions.length < 3 && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {availableOptions.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) handleAddOption(e.target.value); }}
                    className="shopifySelect"
                    style={{ width: 'auto', minWidth: '160px' }}
                    disabled={availableOptions.every(opt => selectedOptions.includes(typeof opt === 'object' ? opt.id : opt))}
                  >
                    <option value="">
                      {availableOptions.every(opt => selectedOptions.includes(typeof opt === 'object' ? opt.id : opt))
                        ? 'All saved options added'
                        : 'Select saved option'}
                    </option>
                    {availableOptions
                      .filter(opt => !selectedOptions.includes(typeof opt === 'object' ? opt.id : opt))
                      .map((opt, i) => {
                        const id = typeof opt === 'object' ? opt.id : opt;
                        const name = typeof opt === 'object' ? opt.name : opt;
                        return <option key={i} value={id}>{name}</option>;
                      })
                    }
                  </select>
                )}
                <input
                  type="text"
                  value={newOptionNameTab}
                  onChange={(e) => setNewOptionNameTab(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const name = newOptionNameTab.trim();
                      if (name) { handleAddOption(name); setNewOptionNameTab(''); }
                    }
                  }}
                  placeholder="Type option name (e.g. Size, Color)"
                  className="shopifyInput"
                  style={{ flex: 1, minWidth: '180px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const name = newOptionNameTab.trim();
                    if (name) { handleAddOption(name); setNewOptionNameTab(''); }
                  }}
                  className="shopifyButton shopifyButtonPrimary"
                >
                  + Add
                </button>
              </div>
            )}
          </div>

          {selectedOptions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666', border: '1px dashed #ccc', borderRadius: '4px' }}>
              Type an option name above (e.g. Size, Color) and click + Add to create product variants
            </div>
          ) : (
            <div>
              {selectedOptions.map(optionId => {
                const opt = availableOptions.find(o => (typeof o === 'object' ? o.id : o) === optionId);
                const optName = typeof opt === 'object' ? opt?.name : opt || optionId;
                const values = optionValues[optionId] || [];
                return (
                  <div key={optionId} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <strong>{optName}</strong>
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(optionId)}
                        style={{ padding: '4px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={currentOptionValue[optionId] || ''}
                        onChange={(e) => setCurrentOptionValue(prev => ({ ...prev, [optionId]: e.target.value }))}
                        onKeyPress={(e) => handleOptionValueKeyPress(e, optionId)}
                        placeholder="Enter value and press Enter"
                        className="shopifyInput"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddOptionValue(optionId, currentOptionValue[optionId])}
                        className="shopifyButton shopifyButtonPrimary"
                      >
                        Add
                      </button>
                    </div>
                    {values.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {values.map((value, index) => (
                          <div key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: '#f0f0f0', borderRadius: '20px', fontSize: '14px' }}>
                            <span>{value}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveOptionValue(optionId, index)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#999', padding: 0, lineHeight: 1 }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                onClick={handleGenerateVariantsFromOptions}
                disabled={!hasNewOptionValues()}
                className="shopifyButton shopifyButtonPrimary"
                style={{ marginTop: '1rem' }}
                title={hasNewOptionValues() ? '' : 'Add new option values to generate additional variants'}
              >
                Generate New Variants
              </button>
              {!hasNewOptionValues() && (
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                  Add new values to options above to generate additional variants
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <VariantTable
        allVariants={allVariants}
        options={formData.options}
        selectedVariantRowIndexes={selectedVariantRowIndexes}
        allMediaItems={allMediaItems}
        variantGroupBy={variantGroupBy}
        expandedVariantGroups={expandedVariantGroups}
        validationErrors={validationErrors}
        variantErrors={variantErrors}
        savingVariantField={savingVariantField}
        handleVariantFieldChange={handleVariantFieldChange}
        toggleProductVariantRowSelection={toggleProductVariantRowSelection}
        handleProductVariantsHeaderSelectionClick={handleProductVariantsHeaderSelectionClick}
        setVariantGroupBy={setVariantGroupBy}
        setExpandedVariantGroups={setExpandedVariantGroups}
        setImagePickerVariant={setImagePickerVariant}
        setShowImagePickerModal={setShowImagePickerModal}
        openVariantDetailsModal={(v) => {
          setVariantDetailDraft({
            ...v,
            variant_name: v.variant_name || v.name || '',
            inventory: v.meta?.inventory || 0,
          });
          setShowVariantDetailModal(true);
        }}
        setHasChanges={setHasChanges}
        expandedVariantId={expandedVariantId}
        setExpandedVariantId={setExpandedVariantId}
        setAllVariants={setAllVariants}
        onDeleteVariant={handleDeleteVariant}
      />
      <ImagePickerModal 
        showImagePickerModal={showImagePickerModal}
        setShowImagePickerModal={setShowImagePickerModal}
        imagePickerVariant={imagePickerVariant}
        allMediaItems={allMediaItems}
        allVariants={allVariants}
        handleVariantImageChange={handleVariantImageChange}
      />
      <VariantDetailsModal 
        showVariantDetailModal={showVariantDetailModal}
        setShowVariantDetailModal={setShowVariantDetailModal}
        variantDetailDraft={variantDetailDraft}
        setVariantDetailDraft={setVariantDetailDraft}
        handleSaveVariantDetails={handleSaveVariantDetails}
        setImagePickerVariant={setImagePickerVariant}
        setShowImagePickerModal={setShowImagePickerModal}
      />
    </div>
  );

  // Render variant selector dropdown
  const renderVariantSelector = () => null;

  const renderOverviewTab = () => (
    <div className="formSection">
      <div className="formGroup">
        <label htmlFor="title">
          Product Title <span className="required">*</span>
          <span className={`characterCounter ${formData.title && formData.title.length > 500 ? 'error' : ''}`}>
            {formData.title ? formData.title.length : 0}/500
          </span>
        </label>
        <input
          id="title"
          name="title"
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          disabled={isReadOnly}
          placeholder="Enter product title"
          className={`shopifyInput ${validationErrors.title ? 'error' : ''} ${formData.title && formData.title.length > 500 ? 'error' : ''}`}
          maxLength={500}
        />
        {validationErrors.title && (
          <span className="fieldError">{validationErrors.title}</span>
        )}
      </div>

      <div className="formGroup">
        <label htmlFor="description">
          Description <span className="required">*</span>
        </label>
        <HTMLEditor
          value={formData.description}
          onChange={(value) => handleInputChange('description', value)}
          placeholder="Enter a detailed product description..."
          disabled={isReadOnly}
          id="description"
        />
        {validationErrors.description && (
          <span className="fieldError">{validationErrors.description}</span>
        )}
      </div>
    </div>
  );

  const renderTagsSection = () => (
    <div className="formGroup">
      <label>Tags {isAdmin && <span className="required">*</span>}</label>
      <div className="tagsInput">
        {(formData.tags || []).map((tag, index) => (
          <span key={index} className="tag">
            {tag}
            {isAdmin && (
              <button
                type="button"
                onClick={() => handleRemoveFromArray('tags', index)}
                className="removeTag"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!isAdmin && (!formData.tags || formData.tags.length === 0) && (
          <span style={{ color: '#999', fontStyle: 'italic' }}>No tags assigned</span>
        )}
        {isAdmin && (
          <input
            type="text"
            placeholder="Add tag..."
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                handleAddToArray('tags', e.target.value.trim());
                e.target.value = '';
              }
            }}
          />
        )}
      </div>
    </div>
  );

  const renderMediaTab = () => (
      <div className="formSection" id="images" style={{ border: validationErrors.images ? '1px solid #d82c0d' : 'none', padding: validationErrors.images ? '1rem' : '0', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Product Images <span className="required">*</span></h3>
          {!isReadOnly && selectedMediaIds.length > 0 && (
            <button
              type="button"
              className="bulkActionButton danger"
              onClick={handleBulkRemoveImages}
              style={{
                padding: '0',
                height: '40px',
                fontSize: '0.9rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
                transition: 'all 0.3s ease',
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#c82333';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.2)';
              }}
            >
              <div style={{
                backgroundColor: 'rgba(0,0,0,0.1)',
                height: '100%',
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.1)'
              }}>
                <span style={{ fontSize: '1.1rem' }}>🗑️</span>
              </div>
              <span style={{ padding: '0 16px' }}>Remove Selected ({selectedMediaIds.length})</span>
            </button>
          )}
        </div>
        {validationErrors.images && (
          <div className="errorMessage" style={{ marginBottom: '1rem', color: '#dc3545' }}>
            {validationErrors.images}
          </div>
        )}

        <div className="imageUploader productDetailImageUploader">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={(formData.images || []).filter((m, idx, arr) => arr.findIndex(x => x.url === m.url) === idx).map(img => img.id || img.url)}
              strategy={rectSortingStrategy}
            >
              {(formData.images || []).filter((m, idx, arr) => arr.findIndex(x => x.url === m.url) === idx).map((image, index) => {
                const imageId = image.id || image.url;

                return (
                  <SortableImage
                    key={imageId}
                    image={image}
                    index={index}
                    isReadOnly={isReadOnly}
                    onRemove={() => handleRemoveFromArray('images', index)}
                    isSelected={selectedMediaIds.includes(imageId)}
                    onToggleSelect={toggleImageSelection}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          {/* Blank image slot at the end for uploading new images */}
          {!isReadOnly && (
            <>
              <input
                id="fileUploadInput"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImage}
                style={{ display: 'none' }}
              />
              <div
                className="imageItem blankImageSlot"
                onClick={() => !uploadingImage && document.getElementById('fileUploadInput').click()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  cursor: uploadingImage ? 'wait' : 'pointer',
                  minHeight: '150px',
                  backgroundColor: uploadingImage ? '#f0f0f0' : '#fafafa',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { if (!uploadingImage) { e.currentTarget.style.borderColor = '#00bfae'; e.currentTarget.style.backgroundColor = '#f0faf9'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.backgroundColor = uploadingImage ? '#f0f0f0' : '#fafafa'; }}
              >
                {uploadingImage ? (
                  <span style={{ fontSize: '1rem', color: '#00bfae' }}>⏳ Uploading...</span>
                ) : (
                  <>
                    <span style={{ fontSize: '2rem', color: '#999', lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>Upload Image</span>
                  </>
                )}
              </div>
              {imageUploadError && (
                <div className="errorMessage" style={{ marginTop: '0.5rem', color: '#dc3545' }}>{imageUploadError}</div>
              )}
            </>
          )}
        </div>
      </div>
  );


  const renderCommissionsTab = () => (
    <div className="formSection">
      <h3>Commissions & Fees</h3>
      <div className="formGrid">
        <div className="formGroup">
          <label htmlFor="brandCommission">Brand Commission (%)</label>
          <input
            id="brandCommission"
            type="number"
            value={formData.brandCommission}
            onChange={(e) => handleInputChange('brandCommission', e.target.value)}
            disabled={isReadOnly}
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="formGroup">
          <label htmlFor="merchantSplit">Merchant Split (%)</label>
          <input
            id="merchantSplit"
            type="number"
            value={formData.merchantSplit}
            onChange={(e) => handleInputChange('merchantSplit', e.target.value)}
            disabled={isReadOnly}
            step="0.01"
            min="0"
            max="100"
          />
        </div>
      </div>
    </div>
  );

  const renderVendorRemarksTab = () => (
    <div className="formSection">
      <h3>Vendor Remarks</h3>
      <div className="formGroup">
        <label htmlFor="vendorRemarks">Private Notes</label>
        <textarea
          id="vendorRemarks"
          value={formData.vendorRemarks}
          onChange={(e) => handleInputChange('vendorRemarks', e.target.value)}
          disabled={isReadOnly}
          placeholder="Private notes and remarks from vendor"
          rows="8"
        />
      </div>
    </div>
  );

  const renderMetaFieldsTab = () => {
    // Build product-level and variant-level meta and merge for display
    const productMeta = { ...(formData.metaFields || {}) };
    const variantMetaRaw = (selectedVariantIndex >= 0 && allVariants[selectedVariantIndex]) ? { ...(allVariants[selectedVariantIndex].meta || {}) } : {};
    // Remove inventory from variant meta as it's handled separately
    if (variantMetaRaw && variantMetaRaw.inventory !== undefined) delete variantMetaRaw.inventory;



    // mergedMeta: product keys take precedence; variant-only keys are included
    const mergedMeta = { ...variantMetaRaw, ...productMeta };

    // Track source for each key to decide where to persist edits ('product' | 'variant')
    // Exclude vendor_remarks from editable meta fields (it's shown in Vendor Remarks section)
    const metaSource = {};
    Object.keys(productMeta).forEach(k => { if (k !== 'vendor_remarks' && k !== 'rejection_reason') metaSource[k] = 'product'; });
    Object.keys(variantMetaRaw).forEach(k => { if (k !== 'inventory' && k !== 'vendor_remarks' && k !== 'rejection_reason' && !metaSource[k]) metaSource[k] = 'variant'; });

    return (
      <div className="formSection">
        <h3>Custom Meta Fields</h3>
        {Object.keys(validationErrors).filter(key => key.startsWith('meta_')).length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            {Object.keys(validationErrors)
              .filter(key => key.startsWith('meta_'))
              .map(key => (
                <div key={key} className="errorMessage" style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                  {validationErrors[key]}
                </div>
              ))}
          </div>
        )}

        <div className="metaFieldsContainer">
          {Object.entries(mergedMeta)
            .filter(([key]) => key !== 'inventory' && key !== 'tags' && key !== 'vendor_remarks' && key !== 'rejection_reason') // Exclude inventory, tags, vendor_remarks and rejection_reason from meta fields display
            .map(([key, value], index) => (
              <div key={key || `meta-${index}`} className="metaFieldRow">
                {isMetaFieldsReadOnly ? (
                  // Vendor view: Display as read-only text
                  <>
                    <div className="metaFieldKey" style={{
                      fontWeight: '600',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      color: '#333'
                    }}>
                      {key && key.startsWith('__new_') ? 'New Field' : key}
                    </div>
                    <div className="metaFieldValue" style={{
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      color: '#333',
                      wordBreak: 'break-word'
                    }}>
                      {typeof value === 'object' ? JSON.stringify(value) : String(value || '')}
                    </div>
                    <div style={{ width: 'auto' }}></div>
                  </>
                ) : (
                  // Admin view: Editable input fields
                  <>
                    <input
                      type="text"
                      defaultValue={key && key.startsWith('__new_') ? '' : key}
                      onBlur={(e) => {
                        const newKey = e.target.value.trim();
                        if (newKey === key) return;

                        // Decide source
                        const source = metaSource[key] === 'variant' ? 'variant' : 'product';

                        if (source === 'variant') {
                          const updatedVariants = [...allVariants];
                          const vMeta = { ...(updatedVariants[selectedVariantIndex].meta || {}) };
                          // Remove old key
                          delete vMeta[key];
                          // If user provided new key, assign latest value
                          if (newKey) vMeta[newKey] = (updatedVariants[selectedVariantIndex].meta || {})[key] ?? '';
                          updatedVariants[selectedVariantIndex] = { ...updatedVariants[selectedVariantIndex], meta: vMeta };
                          setAllVariants(updatedVariants);
                        } else {
                          const newMetaFields = { ...(formData.metaFields || {}) };
                          delete newMetaFields[key];
                          if (newKey) newMetaFields[newKey] = (formData.metaFields || {})[key] ?? '';
                          handleInputChange('metaFields', newMetaFields);
                        }
                        setHasChanges(true);
                      }}
                      placeholder="Field name"
                    />

                    <input
                      type="text"
                      value={value}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        const source = metaSource[key] === 'variant' ? 'variant' : 'product';
                        if (source === 'variant') {
                          const updatedVariants = [...allVariants];
                          const vMeta = { ...(updatedVariants[selectedVariantIndex].meta || {}) };
                          vMeta[key] = newVal;
                          updatedVariants[selectedVariantIndex] = { ...updatedVariants[selectedVariantIndex], meta: vMeta };
                          setAllVariants(updatedVariants);
                          // mark this key as touched by the user
                          setTouchedMetaKeys(prev => Array.from(new Set([...(prev || []), key])));
                        } else {
                          const newMetaFields = { ...(formData.metaFields || {}) };
                          newMetaFields[key] = newVal;
                          handleInputChange('metaFields', newMetaFields);
                          // mark this key as touched by the user
                          setTouchedMetaKeys(prev => Array.from(new Set([...(prev || []), key])));
                        }
                        setHasChanges(true);
                      }}
                      placeholder="Field value"
                    />

                    <button
                      type="button"
                      onClick={() => {
                        // Remove from product-level metaFields
                        const newMetaFields = { ...(formData.metaFields || {}) };
                        if (newMetaFields && newMetaFields.hasOwnProperty(key)) {
                          delete newMetaFields[key];
                          handleInputChange('metaFields', newMetaFields);
                        }

                        // Remove from all variants' meta to ensure deletion persists
                        if (allVariants && allVariants.length > 0) {
                          const updatedVariants = allVariants.map(v => {
                            const vMeta = { ...(v.meta || {}) };
                            if (vMeta && vMeta.hasOwnProperty(key)) delete vMeta[key];
                            return { ...v, meta: vMeta };
                          });
                          setAllVariants(updatedVariants);
                        }

                        // Track deleted key so it won't be reintroduced from variant meta on save
                        setDeletedMetaKeys(prev => Array.from(new Set([...(prev || []), key])));
                        // Remove from touched keys if present
                        setTouchedMetaKeys(prev => (prev || []).filter(k => k !== key));

                        setHasChanges(true);
                      }}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            ))}

          {!isMetaFieldsReadOnly && (
            <button
              type="button"
              onClick={() => {
                // create a stable temporary key so editing the key doesn't collide with other rows
                const newKey = `__new_${Date.now()}_${Math.random().toString(36).slice(2)}`;
                if (selectedVariantIndex >= 0) {
                  // Add to variant meta by default when a variant is selected
                  const updatedVariants = [...allVariants];
                  const vMeta = { ...(updatedVariants[selectedVariantIndex].meta || {}) };
                  vMeta[newKey] = '';
                  updatedVariants[selectedVariantIndex] = { ...updatedVariants[selectedVariantIndex], meta: vMeta };
                  setAllVariants(updatedVariants);
                } else {
                  const newMetaFields = { ...(formData.metaFields || {}) };
                  newMetaFields[newKey] = '';
                  handleInputChange('metaFields', newMetaFields);
                }
                setHasChanges(true);
              }}
              className="addMetaFieldButton"
            >
              + Add Meta Field
            </button>
          )}
        </div>
      </div>
    );
  };

  // Handle export audit logs
  const handleExportLogs = async () => {
    if (!product?.id) return;

    setExportingLogs(true);
    try {
      await exportProductLogs(product.id, product.title || 'product');
      console.log('Product audit logs exported successfully');
    } catch (error) {
      console.error('Error exporting product audit logs:', error);
    } finally {
      setExportingLogs(false);
    }
  };

  const renderAuditHistoryTab = () => {
    const toggleEntry = (index) => {
      setExpandedAuditEntries(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    const formatFieldName = (field) => {
      const fieldMap = {
        'category_id': 'Category',
        'Category Id': 'Category',
        'category': 'Category',
        'description': 'Description',
        'product_state': 'Product State',
        'compare_price': 'Compare At Price',
        'weight_kg': 'Weight (kg)',
        'length_cm': 'Length (cm)',
        'width_cm': 'Width (cm)',
        'height_cm': 'Height (cm)',
        'hs_code': 'HS Code',
        'commission_pct': 'Commission (%)',
        'meta.inventory': 'Inventory',
        'variant_changes': 'Variant Changes'
      };

      // Use mapping if available, otherwise convert to readable format
      if (fieldMap[field]) {
        return fieldMap[field];
      }

      return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\./g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const formatValue = (value, field = null) => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';

      // Special handling for category_id - look up category name
      if (field === 'category_id' || field === 'category' || field === 'Category Id') {
        // If value is already an object with name, use it
        if (typeof value === 'object' && value !== null && value.name) {
          return value.name;
        }
        // Otherwise look up by ID or slug
        const category = categories.find(cat => cat.id === value || cat.slug === value);
        return category ? category.name : value;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '(empty)';
      }

      // Handle objects
      if (typeof value === 'object') {
        if (value.name) return value.name;
        const keys = Object.keys(value);
        if (keys.length === 0) return '(empty)';
        if (keys.length <= 3) {
          return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
        }
        return `{${keys.length} fields}`;
      }

      return String(value);
    };

    const renderChangeDetails = (entry) => {
      const action = entry.action?.toLowerCase();

      // Don't show details for bulk_upload and approved
      if (action === 'bulk_upload' || action === 'approved') {
        return null;
      }

      // For status_change action, show formatted status transition
      if (action === 'status_change') {
        const statusChange = entry.status_change || entry.changes?.status || entry.changes?.status_change;
        if (statusChange && typeof statusChange === 'object') {
          const fromStatus = statusChange.from || statusChange.old || '—';
          const toStatus = statusChange.to || statusChange.new || '—';
          return (
            <div className="changesList">
              <h5>Status Change:</h5>
              <div className="statusChangeDetail">
                <span className="oldValue">{fromStatus}</span>
                <span className="arrow">→</span>
                <span className="newValue">{toStatus}</span>
              </div>
            </div>
          );
        }
        return null;
      }

      // For delete action, show reason
      if (action === 'delete') {
        const reason = entry.changes?.reason || entry.payload?.reason || entry.reason || 'No reason provided';
        const deleteReason = entry.payload?.deleted?.meta?.delete_reason || reason;
        return (
          <div className="changesList">
            <h5>Deletion Details:</h5>
            <div className="deleteReason">
              <strong>Reason:</strong>
              <p>{deleteReason}</p>
            </div>
          </div>
        );
      }




      // For update action, show all changes
      if (action === 'update' || action === 'product_update') {
        if (!entry.changes || Object.keys(entry.changes).length === 0) {
          return <p className="noChanges">No specific changes recorded</p>;
        }

        return (
          <div className="changesList">
            <h5>Changes Made:</h5>
            <ul>
              {Object.entries(entry.changes).map(([field, change]) => {
                // Handle variant_changes - can be array or object with changes array
                if (field === 'variant_changes') {
                  const changesArray = Array.isArray(change) ? change : (change?.changes || []);

                  if (changesArray.length === 0) {
                    return null;
                  }

                  return changesArray.map((variantChange, idx) => {
                    // Handle new variant added - simplified display
                    if (variantChange.action === 'added' && variantChange.field === 'new_variant') {
                      return (
                        <li key={`${field}-${idx}`} className="changeItem variant-change added">
                          new variant added : {variantChange.variant_name || variantChange.sku}
                        </li>
                      );
                    }

                    // Handle variant deletion - simplified display
                    if (variantChange.action === 'deleted') {
                      return (
                        <li key={`${field}-${idx}`} className="changeItem variant-change removed">
                          variant removed : {variantChange.variant_name || variantChange.sku}
                        </li>
                      );
                    }

                    // Handle regular variant field changes
                    return (
                      <li key={`${field}-${idx}`} className="changeItem variant-change">
                        <span className="fieldName">
                          {formatFieldName(variantChange.field)} (SKU: {variantChange.sku}):
                        </span>
                        <span className="changeValue">
                          <span className="oldValue">{variantChange.old === null || variantChange.old === '' ? '—' : variantChange.old}</span>
                          <span className="arrow">→</span>
                          <span className="newValue">{variantChange.new === null || variantChange.new === '' ? '—' : variantChange.new}</span>
                        </span>
                      </li>
                    );
                  });
                }

                // Handle action-based changes (media, meta with action property)
                if (typeof change === 'object' && change.action) {
                  // Handle media changes with detailed tracking
                  if (field.startsWith('media.')) {
                    const parts = field.split('.');
                    const action = parts[1]; // 'add', 'remove', or 'update'

                    if (action === 'add') {
                      return (
                        <li key={field} className="changeItem added">
                          <span className="fieldName">New image added:</span>
                          <span className="changeValue">
                            <span className="newValue">
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              {change.alt_text && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>({change.alt_text})</span>}
                            </span>
                          </span>
                        </li>
                      );
                    } else if (action === 'remove') {
                      return (
                        <li key={field} className="changeItem removed">
                          <span className="fieldName">Image removed:</span>
                          <span className="changeValue">
                            <span className="oldValue">
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              {change.alt_text && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>({change.alt_text})</span>}
                            </span>
                          </span>
                        </li>
                      );
                    } else if (action === 'update') {
                      const subAction = parts[2]; // 'url', 'alt', or undefined for both

                      // Handle URL and alt text both changed
                      if (!subAction && change.old_url && change.new_url) {
                        return (
                          <li key={field} className="changeItem">
                            <span className="fieldName">Image URL and alt text updated:</span>
                            <span className="changeValue">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div>
                                  <strong>URL:</strong>
                                  <span className="oldValue" style={{ marginLeft: '0.5rem' }}>
                                    <a href={change.old_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.old_url.length > 40 ? change.old_url.substring(0, 40) + '...' : change.old_url}
                                    </a>
                                  </span>
                                  <span className="arrow">→</span>
                                  <span className="newValue">
                                    <a href={change.new_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.new_url.length > 40 ? change.new_url.substring(0, 40) + '...' : change.new_url}
                                    </a>
                                  </span>
                                </div>
                                <div>
                                  <strong>Alt:</strong>
                                  <span className="oldValue" style={{ marginLeft: '0.5rem' }}>{change.old_alt || '(none)'}</span>
                                  <span className="arrow">→</span>
                                  <span className="newValue">{change.new_alt || '(none)'}</span>
                                </div>
                              </div>
                            </span>
                          </li>
                        );
                      }

                      // Handle URL only changed
                      if (subAction === 'url' && change.old_url && change.new_url) {
                        return (
                          <li key={field} className="changeItem">
                            <span className="fieldName">Image URL updated:</span>
                            <span className="changeValue">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div>
                                  <span className="oldValue">
                                    <a href={change.old_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.old_url.length > 50 ? change.old_url.substring(0, 50) + '...' : change.old_url}
                                    </a>
                                  </span>
                                </div>
                                <span className="arrow" style={{ margin: '0.25rem 0' }}>↓</span>
                                <div>
                                  <span className="newValue">
                                    <a href={change.new_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.new_url.length > 50 ? change.new_url.substring(0, 50) + '...' : change.new_url}
                                    </a>
                                  </span>
                                </div>
                                {change.alt_text && <span style={{ marginTop: '0.25rem', fontStyle: 'italic', fontSize: '0.85rem', color: '#666' }}>({change.alt_text})</span>}
                              </div>
                            </span>
                          </li>
                        );
                      }

                      // Handle alt text only changed (subAction === 'alt' or legacy format)
                      return (
                        <li key={field} className="changeItem">
                          <span className="fieldName">Image alt text updated:</span>
                          <span className="changeValue">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: '#666', fontSize: '0.85rem', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              <div>
                                <span className="oldValue">{change.old_alt || '(no alt text)'}</span>
                                <span className="arrow">→</span>
                                <span className="newValue">{change.new_alt || '(no alt text)'}</span>
                              </div>
                            </div>
                          </span>
                        </li>
                      );
                    }
                  } else if (field === 'media') {
                    // Legacy media change format (fallback)
                    return (
                      <li key={field} className="changeItem">
                        <span className="fieldName">Media:</span>
                        <span className="changeValue">{change.message || change.action}</span>
                      </li>
                    );
                  } else if (field.startsWith('meta.')) {
                    const metaKey = field.substring(5);

                    // Special handling for tags - skip displaying as generic meta field
                    if (metaKey === 'tags') {
                      if (change.action === 'updated') {
                        const oldTags = Array.isArray(change.old) ? change.old : [];
                        const newTags = Array.isArray(change.new) ? change.new : [];
                        const addedTags = newTags.filter(tag => !oldTags.includes(tag));
                        const removedTags = oldTags.filter(tag => !newTags.includes(tag));

                        return (
                          <React.Fragment key={field}>
                            {addedTags.map((tag, idx) => (
                              <li key={`${field}-added-${idx}`} className="changeItem added tag-change">
                                <span className="fieldName">New tag added:</span>
                                <span className="changeValue">
                                  <span className="newValue">{tag}</span>
                                </span>
                              </li>
                            ))}
                            {removedTags.map((tag, idx) => (
                              <li key={`${field}-removed-${idx}`} className="changeItem removed tag-change">
                                <span className="fieldName">Tag removed:</span>
                                <span className="changeValue">
                                  <span className="oldValue">{tag}</span>
                                </span>
                              </li>
                            ))}
                          </React.Fragment>
                        );
                      }
                      // Skip rendering tags for 'added' or 'removed' actions - they're handled above
                      return null;
                    }

                    if (change.action === 'added') {
                      return (
                        <li key={field} className="changeItem added">
                          <span className="fieldName">New meta field added:</span>
                          <span className="changeValue">
                            <span className="newValue">{metaKey}: {change.value}</span>
                          </span>
                        </li>
                      );
                    } else if (change.action === 'removed') {
                      return (
                        <li key={field} className="changeItem removed">
                          <span className="fieldName">Removed meta field:</span>
                          <span className="changeValue">
                            <span className="oldValue">{metaKey}: {change.old_value}</span>
                          </span>
                        </li>
                      );
                    } else if (change.action === 'updated') {
                      return (
                        <li key={field} className="changeItem">
                          <span className="fieldName">Meta field updated:</span>
                          <span className="changeValue">
                            <span className="oldValue">{metaKey}: {change.old}</span>
                            <span className="arrow">→</span>
                            <span className="newValue">{metaKey}: {change.new}</span>
                          </span>
                        </li>
                      );
                    }
                    // Meta field handled, don't fall through to regular field handling
                    return null;
                  }
                }

                // Regular field change with old/new values
                if (typeof change === 'object' && (change.old !== undefined || change.new !== undefined)) {
                  return (
                    <li key={field} className="changeItem">
                      <span className="fieldName">{formatFieldName(field)}:</span>
                      <span className="changeValue">
                        <span className="oldValue">{formatValue(change.old, field)}</span>
                        <span className="arrow">→</span>
                        <span className="newValue">{formatValue(change.new, field)}</span>
                      </span>
                    </li>
                  );
                }

                // Legacy format with from/to
                if (typeof change === 'object' && (change.from !== undefined || change.to !== undefined)) {
                  return (
                    <li key={field} className="changeItem">
                      <span className="fieldName">{formatFieldName(field)}:</span>
                      <span className="changeValue">
                        <span className="oldValue">{formatValue(change.from, field)}</span>
                        <span className="arrow">→</span>
                        <span className="newValue">{formatValue(change.to, field)}</span>
                      </span>
                    </li>
                  );
                }

                // Fallback for simple values
                return (
                  <li key={field} className="changeItem">
                    <span className="fieldName">{formatFieldName(field)}:</span>
                    <span className="changeValue">{JSON.stringify(change)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      }

      // For other actions, show changes if available
      if (entry.changes && Object.keys(entry.changes).length > 0) {
        return (
          <div className="changesList">
            <h5>Details:</h5>
            <ul>
              {Object.entries(entry.changes).map(([field, change]) => (
                <li key={field} className="changeItem">
                  <span className="fieldName">{formatFieldName(field)}:</span>
                  <span className="changeValue">{JSON.stringify(change)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      return null;
    };

    return (
      <div className="formSection">
        <h3 style={{
          margin: '0 0 1.5rem 0',
          color: '#333',
          fontSize: '1.25rem',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Audit & History</span>
          {isAdmin && (
            <button
              onClick={handleExportLogs}
              disabled={exportingLogs}
              style={{
                background: '#F76B1C',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: exportingLogs ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(247, 107, 28, 0.2)',
                opacity: exportingLogs ? 0.6 : 1,
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseOver={(e) => !exportingLogs && (e.currentTarget.style.background = '#e55a0f')}
              onMouseOut={(e) => !exportingLogs && (e.currentTarget.style.background = '#F76B1C')}
            >
              {exportingLogs ? (
                <>
                  <span style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.6s linear infinite'
                  }}></span>
                  Exporting...
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Export to Excel
                </>
              )}
            </button>
          )}
        </h3>
        <div className="auditHistory">
          {formData.auditHistory.length === 0 ? (
            <p className="noHistory">No audit history available</p>
          ) : (
            <div className="auditTimeline">
              {formData.auditHistory.map((entry, index) => {
                const isExpanded = expandedAuditEntries[index];
                const action = entry.action?.toLowerCase();
                const hasDetails = action === 'update' || action === 'product_update' || action === 'request_changes' || action === 'reject' || action === 'delete' || action === 'status_change';

                return (
                  <div key={index} className={`auditEntry ${isExpanded ? 'expanded' : ''}`}>
                    <div
                      className="auditEntryHeader clickable"
                      onClick={() => toggleEntry(index)}
                    >
                      <div className="headerLeft">
                        <span className={`auditAction action-${action}`}>
                          {entry.action?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        {hasDetails && (
                          <span className="expandIcon">{isExpanded ? '▼' : '▶'}</span>
                        )}
                      </div>
                      <div className="headerRight">
                        <span className="auditDate">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="auditEntryDetails">
                        <div className="detailsGrid">
                          <div className="detailItem">
                            <span className="detailLabel">User:</span>
                            <span className="detailValue">{entry.changed_by?.name || entry.user || 'Unknown'}</span>
                          </div>
                          <div className="detailItem">
                            <span className="detailLabel">Role:</span>
                            <span className="detailValue">{entry.changed_by?.role || entry.user_role || 'Unknown'}</span>
                          </div>
                          <div className="detailItem">
                            <span className="detailLabel">Time:</span>
                            <span className="detailValue">
                              {new Date(entry.timestamp).toLocaleString('en-US', {
                                dateStyle: 'medium',
                                timeStyle: 'short'
                              })}
                            </span>
                          </div>
                        </div>

                        {entry.reason && (
                          <div className="reasonSection">

                            <strong>{(entry.action === 'request_changes') ? 'Changes requested' : (entry.action === 'reject' ? 'Rejection reason' : 'Reason')}:</strong>
                            {(() => {
                              const raw = entry.reason || '';
                              const items = raw.split(';').map(s => s.trim()).filter(Boolean);
                              if (items.length > 1) {
                                return (
                                  <ul className="reasonList">
                                    {items.map((it, idx) => (
                                      <li key={idx}>{it}</li>
                                    ))}
                                  </ul>
                                );
                              }
                              return <p>{raw}</p>;
                            })()}
                          </div>
                        )}

                        {renderChangeDetails(entry)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="productDetailView">
      <div className="detailViewHeader">
        <div className="headerLeft">
          <h2>{isReadOnly ? 'View Product' : 'Edit Product'}</h2>
          <span className="productId">ID: {product?.id}</span>
        </div>
        <div className="headerRight">
          {hasChanges && !isReadOnly && (
            <span className="unsavedChanges">Unsaved changes</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="closeButton"
          >
            ×
          </button>
        </div>
      </div>

      <div className="detailViewContent" style={{ padding: '1.5rem' }}>
        {/* Validation Errors Summary Banner */}
        {Object.keys(validationErrors).length > 0 && showValidationErrors && (
          <div className="validationSummaryBanner error">
            <div className="bannerIcon">⚠️</div>
            <div className="bannerContent">
              <h4>Please fix the following issues:</h4>
              <ul className="errorList">
                {Object.entries(validationErrors).map(([field, message]) => (
                  <li key={field}>
                    <strong>{field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}:</strong> {message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Admin Changes Notification */}
        {recentAdminChanges.length > 0 && (
          <div className="updatedFieldsAlert" style={{ marginBottom: '1rem' }}>
            <div className="alertHeader">
              <h4>📋 Admin Has Requested Changes</h4>
            </div>
            <p className="alertDescription">Admin has requested new changes. Check the audit log for more information.</p>
            <ul>
              {recentAdminChanges.map((change, idx) => (
                <li key={idx}>
                  <strong>
                    {change.action === 'request_changes' ? '📝 Request Changes:' : '❌ Rejected:'}
                  </strong>{' '}
                  <span>{change.reason}</span>
                  {change.changed_by && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                      (by {change.changed_by.name})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Requested Changes Banner */}
        {requestedChanges.length > 0 && showRequestedBanner && (
          <div className="validationSummaryBanner warning" style={{ marginTop: '0.5rem' }}>
            <div className="bannerIcon">📝</div>
            <div className="bannerContent">
              <h4>Changes Requested:</h4>
              <ul className="reasonList changesList">
                {requestedChanges.filter(c => c.field !== 'general').map((change, index) => {
                  const raw = change.message || '';
                  const items = raw.split(';').map(s => s.trim()).filter(Boolean);
                  if (items.length > 1) {
                    return (
                      <li key={index} className="noMarker">
                        <strong>{change.field}:</strong>
                        <ul className="reasonList">
                          {items.map((it, i2) => (
                            <li key={i2}>{it}</li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  return (
                    <li key={index}>
                      <strong>{change.field}:</strong> {raw}
                    </li>
                  );
                })}

                {requestedChanges.filter(c => c.field === 'general').map((change, index) => {
                  const raw = change.message || '';
                  const items = raw.split(';').map(s => s.trim()).filter(Boolean);
                  if (items.length > 1) {
                    return (
                      <li key={`general-${index}`} className="noMarker">
                        <ul className="reasonList">
                          {items.map((it, i2) => (
                            <li key={i2}>{it}</li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  return <li key={`general-${index}`}>{raw}</li>;
                })}
              </ul>
            </div>
          </div>
        )}

        <div className="detailViewContent">
        {/* Toast Notification */}
        {toast.show && (
          <div className={`shopifyToast ${toast.type}`}>
            {toast.type === 'success' ? '✓' : '⚠'} {toast.message}
          </div>
        )}

        {/* Validation Errors Summary Banner */}
        {Object.keys(validationErrors).length > 0 && showValidationErrors && (
          <div className="errorBanner">
            <div className="errorBannerIcon">⚠</div>
            <div className="errorBannerContent">
              <h4>Please fix the following errors:</h4>
              <ul>
                {Object.entries(validationErrors)
                  .filter(([key]) => !key.startsWith('variant_')) // Don't list every variant error in the summary
                  .map(([key, msg]) => (
                  <li key={key}>{msg}</li>
                ))}
                {Object.keys(validationErrors).some(k => k.startsWith('variant_')) && (
                  <li>There are errors in the Variants table. Please check SKU and Price for each row.</li>
                )}
              </ul>
            </div>
          </div>
        )}
          {!product || !isFormInitialized ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <div className="spinner"></div>
              <p>Loading product details...</p>
            </div>
          ) : (
            <div className="shopifyLayout">
              {/* Main Column */}
              <div className="mainColumn">
                {/* Title and Description */}
                <div className="shopifyCard">
                  <div className="formGroup">
                    <label htmlFor="title">Title</label>
                    <input
                      id="title"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Short sleeve t-shirt"
                      className={`shopifyInput ${validationErrors.title ? 'error' : ''}`}
                    />
                    {validationErrors.title && <span className="fieldError">{validationErrors.title}</span>}
                  </div>

                  <div className="formGroup">
                    <label htmlFor="description">Description</label>
                    <HTMLEditor
                      value={formData.description}
                      onChange={(value) => handleInputChange('description', value)}
                      disabled={isReadOnly}
                      placeholder="Provide a detailed description of your product..."
                    />
                    {showValidationErrors && !formData.description && <span className="fieldError">Description is required</span>}
                  </div>
                </div>

                {/* Media Section */}
                <div className="shopifyCard">
                  <div className="shopifyCardHeader">
                    <h3>Media</h3>
                  </div>
                  {renderMediaTab()}
                </div>

                {/* Variants & Options Section */}
                <div className="shopifyCard">
                  <div className="shopifyCardHeader">
                    <h3>Variants</h3>
                  </div>
                  {renderInventoryPricingTab()}
                </div>

              </div>

              {/* Side Column */}
              <div className="sideColumn">
                {/* Status Card */}
                <div className="shopifyCard statusCard">
                  <div className="shopifyCardHeader">
                    <h3>Product Status</h3>
                  </div>
                  {!isReadOnly ? (
                    <StatusDropdown
                      currentStatus={formData.status}
                      onSelect={(value) => handleInputChange('status', value)}
                    />
                  ) : (
                    <div className="statusBadgeContainer">
                      <span className={`statusTag ${catalogStatusClass(product.terminal_status)}`}>
                        {formatStatusLabel(product.terminal_status)}
                      </span>
                    </div>
                  )}
                  {!isReadOnly && formData.status !== product.terminal_status && (
                    <p className="statusDescription statusActionHint">
                      Save to submit this status change for admin approval.
                    </p>
                  )}
                </div>

                {/* Organization Card */}
                <div className="shopifyCard">
                  <div className="shopifyCardHeader">
                    <h3>Product Organization</h3>
                  </div>

                  <div className="formGroup">
                    <label htmlFor="category">Category <span className="required">*</span></label>
                    <select
                      id="category"
                      name="category"
                      value={formData.categoryId || ''}
                      onChange={(e) => handleInputChange('categoryId', e.target.value)}
                      disabled={isReadOnly || loadingCategories}
                      className={`shopifySelect ${validationErrors.category ? 'error' : ''}`}
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                    {validationErrors.category && (
                      <span className="fieldError">{validationErrors.category}</span>
                    )}
                  </div>

                  <div className="formGroup">
                    <label>Tags</label>
                    <div className="tagsInput shopifyTagsInput">
                      {(formData.tags || []).map((tag, index) => (
                        <span key={index} className="tag shopifyTag">
                          {tag}
                          {!isReadOnly && (
                            <button type="button" onClick={() => handleRemoveFromArray('tags', index)} className="removeTag">×</button>
                          )}
                        </span>
                      ))}
                      {!isReadOnly && (
                        <input
                          type="text"
                          placeholder="Add tag..."
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              handleAddToArray('tags', e.target.value.trim());
                              e.target.value = '';
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="formGroup">
                    <label>Vendor</label>
                    <div className="vendorDisplay">
                      {formData.vendorName || 'No Vendor Assigned'}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>

      {!isReadOnly && (
        <div className="detailViewFooter shopifyFooter">
          <div className="footerLeft">
            {hasChanges && <span className="unsavedChangesText">Unsaved changes</span>}
          </div>
          <div className="footerRight">
            <button type="button" onClick={onClose} className="shopifyButtonSecondary" disabled={isSubmitting}>
              Cancel
            </button>
            
            {/* Show Save Changes only if no changes have been made or if push to pending is not possible */}
            {(!hasChanges || hideApprovalButton || product.state === 'pending') && (
              <button
                type="button"
                onClick={handleSave}
                className="shopifyButtonPrimary"
                disabled={isSubmitting || !hasChanges}
              >
                {isSubmitting && !isPushToPendingApproval ? (
                  <><span className="spinner"></span> Saving...</>
                ) : 'Save Changes'}
              </button>
            )}

            {/* Show Push to Pending Approval only if changes have been made and it's not already pending */}
            {!hideApprovalButton && product.state !== 'pending' && hasChanges && (
              <button 
                type="button" 
                onClick={handlePushToPendingApproval} 
                className="shopifyButtonPrimary"
                style={{ backgroundColor: '#000' }}
                disabled={isSubmitting}
              >
                {isSubmitting && isPushToPendingApproval ? (
                  <><span className="spinner"></span> Submitting...</>
                ) : 'Push to Pending Approval'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="modalOverlay" onClick={handleCancelSave}>
          <div className="modalContent shopifyModal" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h2>{isPushToPendingApproval ? 'Push to Pending Approval' : 'Provide Reason for Changes'}</h2>
              <button onClick={handleCancelSave} className="modalClose">×</button>
            </div>
            <div className="modalBody">
              <p>{isPushToPendingApproval ? 'This will save your changes and submit for approval.' : 'Please provide a reason for these changes.'}</p>
              <textarea
                value={saveReason}
                onChange={(e) => setSaveReason(e.target.value)}
                placeholder="E.g., Updated pricing based on market trends..."
                rows={4}
                className="shopifyTextarea"
              />
            </div>
            <div className="modalFooter">
              <button onClick={handleCancelSave} className="shopifyButtonDiscard">Cancel</button>
              <button
                onClick={handleConfirmSave}
                className="shopifyButtonPrimary"
                disabled={!saveReason.trim() || saving}
              >
                {saving ? 'Saving...' : (isPushToPendingApproval ? 'Submit' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailView;
export { mapFormDataToApiPayload, StatusDropdown };
